import { themedConfirm, themedPrompt } from '../../../lib/themed-dialogs';
import { pickReachable } from '../../../lib/url-reachability';
import { instanceToolCall } from '../../../lib/project-tool-name';
// Supabase Module — správa Supabase instancí
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import i18n from '../../../lib/i18n';
import { useLiveProp } from '../../../lib/useLiveProp';
import { callWidgetTool } from '../../../widgets/scope';
import type { EffectiveScope, WidgetRenderProps } from '../../../widgets/types';

/** Scope widgetu — nastaví shell přes *Panel wrapper (node-aware MCP). */
let _widgetScope: EffectiveScope = { node: null, identity: null };
export function setSupabaseWidgetScope(scope: EffectiveScope) {
  _widgetScope = scope;
}
function callMcp<T = unknown>(
  name: string,
  args: Record<string, unknown> = {},
  opts?: { silent?: boolean; label?: string },
): Promise<T> {
  return callWidgetTool(name, args, _widgetScope, opts) as Promise<T>;
}
function scopeKey() {
  return _widgetScope.node ?? 'local';
}

import { toast } from 'sonner';
import {
  Alert,
  AsyncStatusToggle,
  BackupPanel,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  DeployLogViewer,
  EmptyState,
  LifecycleControls,
  Modal,
  Select,
  StatCard,
  StatsGrid,
  Tab,
  TabList,
  TabPanel,
  Tabs,
} from '../../../components/ui';
import { Label } from '../../../components/ui/Label';
import { NodeSelector } from '../../../components/NodeSelector';
import {
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Container,
  Copy,
  Database,
  ExternalLink,
  Eye,
  EyeOff,
  FileText,
  Globe,
  Layers,
  Loader2,
  Lock,
  Plus,
  RefreshCw,
  Settings,
  Shield,
  ShieldAlert,
  ShieldCheck,
  ShieldOff,
  Table2,
  Trash2,
  UserPlus,
  Users,
  X,
  XCircle,
  Zap,
} from 'lucide-react';

/**
 * Overlay modalů PATŘÍ do document.body. Karty widgetů mají backdrop-filter
 * (acm-glass) → stávají se containing blockem pro position:fixed a surový
 * overlay se vykreslí JEN uvnitř karty: „okno není navrchu a není celé"
 * (hlášeno na /core/supabase, 2026-09-02). Sdílený ui/Modal to řeší portálem;
 * dokud sem nedoputuje plná migrace na něj, portáluje tenhle wrapper.
 */
function BodyOverlay({ children }: { children: ReactNode }) {
  return createPortal(children, document.body);
}

interface CertInfo {
  expires_on: string;
  days_left: number;
  valid: boolean;
}

interface SupabaseInstance {
  name: string;
  dir: string;
  legacy: boolean;
  total: number;
  running: number;
  healthy?: number;
  unhealthy?: number;
  status: 'healthy' | 'running' | 'partial' | 'stopped' | 'error';
  ports: string[];
  error?: string;
  // publish state
  private_api: string | null;
  private_studio: string | null;
  // 3 layers přístupu — viz supabase_list URL builder.
  //   raw_*: přímý kong/studio na 127.0.0.1 (HTTP, žádný auth)
  //   auth_*: ACM nginx wrapper s auth_request + HTTPS (jen pokud
  //           /etc/nginx/supabase.d/<name>.conf existuje)
  //   public_*: NPM published URL (jen pokud studio/api_public)
  raw_api?: string | null;
  raw_studio?: string | null;
  auth_api?: string | null;
  auth_studio?: string | null;
  auth_nginx_deployed?: boolean;
  // Scope per port — 'internal' = bind 127.0.0.1 (= host loopback only),
  // 'public' = bind 0.0.0.0 (= dostupné na host IP). Vrací supabase_list
  // od MR #177. UI renderuje badge co odlišuje typy přístupu.
  api_scope?: 'internal' | 'public';
  studio_scope?: 'internal' | 'public';
  url_hash: string | null;
  api_public: boolean;
  studio_public: boolean;
  api_public_enabled: boolean;
  studio_public_enabled: boolean;
  // Jak je Studio dosažitelné zvenku: 'off' nepublikováno, 'shadow' jen z adres,
  // ze kterých je někdo přihlášený na UI uzlu, 'public' bez omezení adresy.
  studio_access?: 'off' | 'shadow' | 'public';
  // Vazba na projekt orchestrátoru. Když existuje, akce se MUSÍ volat
  // projektově kvalifikovaně — viz lib/project-tool-name.ts.
  project?: { project_id: string; project_name?: string; service_key: string } | null;
  api_domain: string | null;
  studio_domain: string | null;
  // SSO state pro Studio (catalog extras.sso_*)
  studio_sso_enabled?: boolean;
  studio_sso_registered?: boolean;
  studio_sso_client_id?: string | null;
  public_api: string | null;
  public_studio: string | null;
  // cert info
  api_cert: CertInfo | null;
  studio_cert: CertInfo | null;
  // db schemas
  db_schemas?: string;
  started_at?: string | null;
  memory_mb?: number;
  disk_mb?: number;
  auth_api_port?: number | null;
  auth_studio_port?: number | null;
}

interface SupabaseListResult {
  ok: boolean;
  instances: SupabaseInstance[];
  total: number;
}

interface FullCreds {
  ok: boolean;
  instance: string;
  api_url: string;
  anon_key: string;
  service_role_key: string;
  jwt_secret: string;
  db_url: string;
  kong_port: number;
  db_port: number;
}

const STATUS_MAP: Record<string, { cls: string; label: string }> = {
  healthy: { cls: 'text-emerald-400 bg-emerald-400/10 border-emerald-500/20', label: 'healthy' },
  running: { cls: 'text-blue-400 bg-blue-400/10 border-blue-500/20', label: 'running' },
  provisioning: {
    cls: 'text-amber-400 bg-amber-400/10 border-amber-500/20 animate-pulse',
    label: 'provisioning',
  },
  deleting: {
    cls: 'text-red-400 bg-red-400/10 border-red-500/20 animate-pulse',
    label: 'deleting',
  },
  delete_failed: {
    cls: 'text-red-400 bg-red-400/10 border-red-500/20',
    label: 'delete failed',
  },
  partial: { cls: 'text-amber-400 bg-amber-400/10 border-amber-500/20', label: 'partial' },
  stopped: { cls: 'text-gray-500 bg-gray-800 border-gray-700', label: 'stopped' },
  error: { cls: 'text-red-400 bg-red-400/10 border-red-500/20', label: 'error' },
};

function StatusBadge({ status }: { status: SupabaseInstance['status'] }) {
  const { t } = useTranslation();
  const s = STATUS_MAP[status] ?? STATUS_MAP.error;
  const label = (status as string) === 'provisioning'
    ? t('supabase.statusProvisioning')
    : (status as string) === 'deleting'
    ? t('supabase.statusDeleting')
    : s.label;
  return (
    <span className={`text-xs px-2 py-0.5 rounded-lg border font-medium ${s.cls}`}>{label}</span>
  );
}


// Sync activity badge — polluje supabase.sync_status_poll každých 10s pro
// instanci kterou user vidí. Background system_task běží na 120s, takže
// základní stav je vždy v catalog i bez otevřeného UI. Tady jen rychlejší
// refresh když user kouká.
function SyncBadge({ instanceName }: { instanceName: string }) {
  const { t } = useTranslation();
  const { data } = useQuery({
    queryKey: ['supabase-sync-state', instanceName],
    queryFn: async () => {
      const parsed = await callMcp<{
        ok: boolean;
        error?: string;
        polled?: number;
        snapshots?: Array<{
          instance: string;
          ts: number;
          storage_objects: number;
          public_rows: number;
          delta_storage: number;
          delta_rows: number;
          sync_active: boolean;
          last_activity_ts: number | null;
        }>;
      }>('supabase.sync_status_poll',
        { name: instanceName },
      );
      return parsed?.snapshots?.[0] ?? null;
    },
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
    staleTime: 5_000,
  });

  if (!data) return null;
  const activeNow = data.sync_active;
  const sinceMs = data.last_activity_ts ? Date.now() - data.last_activity_ts : null;
  const recentlyActive = sinceMs !== null && sinceMs < 5 * 60_000; // 5 min

  if (activeNow) {
    return (
      <span
        className='text-[10px] px-2 py-0.5 rounded-lg border font-medium bg-green-500/15 text-green-300 border-green-500/30'
        title={t('supabase.syncActiveTitle', { objects: data.delta_storage, rows: data.delta_rows })}
      >
        {t('supabase.syncActiveBadge', { objects: data.delta_storage, rows: data.delta_rows })}
      </span>
    );
  }
  if (recentlyActive && sinceMs !== null) {
    const min = Math.floor(sinceMs / 60_000);
    return (
      <span
        className='text-[10px] px-2 py-0.5 rounded-lg border font-medium bg-amber-500/15 text-amber-300 border-amber-500/30'
        title={t('supabase.syncIdleTitle', { min, objects: data.storage_objects, rows: data.public_rows })}
      >
        {t('supabase.syncIdleBadge', { min })}
      </span>
    );
  }
  return null;
}

function copyText(t: string) {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(t).then(() => toast.success(i18n.t('supabase.copied'))).catch(() =>
      copyFallback(t)
    );
  } else copyFallback(t);
}
function copyFallback(t: string) {
  const el = document.createElement('textarea');
  el.value = t;
  el.style.cssText = 'position:fixed;top:-9999px;left:-9999px';
  document.body.appendChild(el);
  el.select();
  try {
    document.execCommand('copy');
    toast.success(i18n.t('supabase.copied'));
  } catch {
    toast.error(i18n.t('supabase.copyFailed'));
  }
  document.body.removeChild(el);
}

function SecretField({ label, value }: { label: string; value: string }) {
  const [show, setShow] = useState(false);
  return (
    <div>
      <label className='text-xs text-gray-500 mb-1 block'>{label}</label>
      <div className='flex items-center gap-1.5 bg-gray-800 rounded-lg px-2 py-1.5'>
        <code className='flex-1 text-xs font-mono text-gray-300 truncate'>
          {show ? value : '••••••••••••••••'}
        </code>
        <button
          onClick={() => setShow((s) => !s)}
          className='text-gray-600 hover:text-gray-400 flex-shrink-0'
        >
          {show ? <EyeOff size={12} /> : <Eye size={12} />}
        </button>
        <button
          onClick={() => copyText(value)}
          className='text-gray-600 hover:text-gray-400 flex-shrink-0'
        >
          <Copy size={12} />
        </button>
      </div>
    </div>
  );
}

function CertBadge({ cert, label }: { cert: CertInfo | null; label: string }) {
  const { t } = useTranslation();
  if (!cert) return null;
  const warn = cert.days_left <= 14;
  const expired = !cert.valid;
  const Icon = expired ? ShieldOff : warn ? ShieldAlert : ShieldCheck;
  const cls = expired
    ? 'text-red-400 bg-red-400/10 border-red-500/20'
    : warn
    ? 'text-amber-400 bg-amber-400/10 border-amber-500/20'
    : 'text-emerald-400 bg-emerald-400/10 border-emerald-500/20';
  const expiresStr = new Date(cert.expires_on).toLocaleDateString('cs-CZ', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
  return (
    <span
      className={`flex items-center gap-1 text-[10px] px-1.5 py-0.5 rounded border font-medium ${cls}`}
      title={t('supabase.certValidUntil', { label, date: expiresStr })}
    >
      <Icon size={10} />
      {expired ? t('supabase.certExpired') : `${cert.days_left}d`}
    </span>
  );
}

function UrlRow(
  { label, url, isPublic }: { label: string; url: string | null; isPublic?: boolean },
) {
  if (!url) return null;
  return (
    <div className='flex items-center justify-between gap-2 text-xs'>
      <span className='text-gray-500 flex-shrink-0'>{label}</span>
      <div className='flex items-center gap-1 min-w-0'>
        {isPublic && <Globe size={11} className='text-emerald-400 flex-shrink-0' />}
        <code className={`font-mono truncate ${isPublic ? 'text-emerald-400' : 'text-blue-400'}`}>
          {url}
        </code>
        <button
          onClick={() => copyText(url)}
          className='text-gray-600 hover:text-gray-400 flex-shrink-0 ml-1'
        >
          <Copy size={11} />
        </button>
        {isPublic && (
          <a
            href={url}
            target='_blank'
            rel='noopener noreferrer'
            className='text-gray-600 hover:text-gray-400 flex-shrink-0'
          >
            <ExternalLink size={11} />
          </a>
        )}
      </div>
    </div>
  );
}

/** Scope widgetu — nastaví shell přes *Panel wrapper (node-aware MCP). */
interface EdgeFunctionInfo {
  name: string;
  env_keys: string[];
  missing_keys?: string[];
  enabled?: boolean;
}

interface FunctionProbeEntry {
  name: string;
  running: boolean;
  status: number | null;
  detail?: string;
}

interface FunctionsProbeResult {
  ok: boolean;
  instance?: string;
  total: number;
  running_count: number;
  down_count: number;
  results: FunctionProbeEntry[];
  down: string[];
  error?: string;
}

interface FunctionsInfoResult {
  ok: boolean;
  instance?: string;
  edge_container?: string | null;
  edge_running?: boolean;
  functions_count?: number;
  functions?: EdgeFunctionInfo[];
  referenced_keys?: string[];
  set_keys?: string[];
  missing_keys?: string[];
  extra_set_keys?: string[];
  error?: string;
}

// Edge funkce / Secrets — data-driven. Načte supabase.functions_info → ukáže
// reálné funkce + jejich required secret keys. Pro KAŽDÝ referenced_key jeden
// řádek (uživatel vidí pojmenované secrety, ne prázdný řádek). Hodnoty jsou
// write-only (zapisují se do edge kontejneru, recreate); prázdné pole = beze změny.
function EdgeFunctionsPanel({ instanceName }: { instanceName: string }) {
  const { t } = useTranslation();
  const info = useQuery<FunctionsInfoResult>({
    queryKey: ['supabase-fns-info', instanceName],
    queryFn: () => callMcp('supabase.functions_info', { name: instanceName }),
  });

  // value inputs — keyed by secret key name. Custom keys (added přes "+ vlastní
  // klíč") žijí v customKeys; jejich hodnoty taky ve `values`.
  const [values, setValues] = useState<Record<string, string>>({});
  const [customKeys, setCustomKeys] = useState<string[]>([]);
  const [sourceDir, setSourceDir] = useState('');
  // On-demand runtime probe výsledek (supabase.functions_probe). null = netestováno.
  const [probe, setProbe] = useState<FunctionsProbeResult | null>(null);
  // Per-function busy stav pro enable/disable toggle (název funkce, nebo null).
  const [togglingFn, setTogglingFn] = useState<string | null>(null);

  const data = info.data;
  const functions = data?.functions ?? [];
  const referencedKeys = data?.referenced_keys ?? [];
  const setKeys = new Set(data?.set_keys ?? []);
  const missingKeys = new Set(data?.missing_keys ?? []);
  const extraSetKeys = data?.extra_set_keys ?? [];

  // Mapuje klíč → seznam funkcí, které ho čtou (pro počet + tooltip).
  const keyToFns: Record<string, string[]> = {};
  for (const fn of functions) {
    for (const k of fn.env_keys) {
      (keyToFns[k] ??= []).push(fn.name);
    }
  }

  // Řádky: všechny referenced_keys + custom keys (deduplikace).
  const rowKeys: string[] = [...referencedKeys];
  for (const k of customKeys) if (!rowKeys.includes(k)) rowKeys.push(k);

  const saveSecrets = useMutation({
    mutationFn: () => {
      const obj: Record<string, string> = {};
      for (const [k, v] of Object.entries(values)) {
        const key = k.trim();
        if (key && v !== '') obj[key] = v;
      }
      return callMcp<{ ok: boolean; error?: string }>('supabase.set_function_secrets', {
        name: instanceName,
        secrets: JSON.stringify(obj),
      });
    },
    onSuccess: (r: { ok: boolean; error?: string }) => {
      if (!r.ok) {
        toast.error(r.error ?? t('supabase.saveFailed'));
        return;
      }
      toast.success(t('supabase.secretsSaved'));
      setValues({});
      info.refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deployFunctions = useMutation({
    mutationFn: () =>
      callMcp<{
        ok: boolean;
        copied_count?: number;
        changed?: { added: string[]; modified: string[]; unchanged: string[]; removed: string[] };
        error?: string;
      }>('supabase.deploy_functions', { name: instanceName, source_dir: sourceDir }),
    onSuccess: (
      r: {
        ok: boolean;
        copied_count?: number;
        changed?: { added: string[]; modified: string[]; unchanged: string[]; removed: string[] };
        error?: string;
      },
    ) => {
      if (!r.ok) {
        toast.error(r.error ?? t('supabase.redeployFailed'));
        return;
      }
      const c = r.changed;
      const detail = c
        ? `+${c.added.length} ~${c.modified.length} =${c.unchanged.length} -${c.removed.length}`
        : t('supabase.filesCount', { count: r.copied_count ?? 0 });
      toast.success(t('supabase.functionsDeployed', { detail }));
      info.refetch();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Runtime probe — on demand (může trvat ~10-40s pro hodně funkcí).
  const probeFunctions = useMutation({
    mutationFn: () =>
      callMcp('supabase.functions_probe', { name: instanceName }) as Promise<FunctionsProbeResult>,
    onSuccess: (r: FunctionsProbeResult) => {
      if (!r.ok) {
        toast.error(r.error ?? t('supabase.functionProbeFailed'));
        return;
      }
      setProbe(r);
      if (r.down_count > 0) {
        toast.error(t('supabase.functionsDownToast', { count: r.down_count, names: r.down.join(', ') }));
      } else {
        toast.success(t('supabase.allFunctionsRunning', { count: r.running_count }));
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Enable/disable jednotlivé funkce (dir rename <name>.disabled). Per-row busy
  // přes togglingFn; po úspěchu refetch functions_info aby se řádek překreslil.
  const toggleFunction = useMutation({
    mutationFn: (vars: { name: string; enabled: boolean }) =>
      callMcp('supabase.set_function_enabled', {
        instance: instanceName,
        function: vars.name,
        enabled: vars.enabled,
      }) as Promise<{
        ok: boolean;
        instance?: string;
        function?: string;
        enabled?: boolean;
        disabled_functions?: string[];
        error?: string;
      }>,
    onMutate: (vars) => setTogglingFn(vars.name),
    onSuccess: (r, vars) => {
      if (!r.ok) {
        toast.error(r.error ?? t('supabase.functionToggleFailed'));
        return;
      }
      toast.success(
        vars.enabled
          ? t('supabase.functionEnabledToast', { name: vars.name })
          : t('supabase.functionDisabledToast', { name: vars.name }),
      );
      info.refetch();
    },
    onError: (e: Error) => toast.error(e.message),
    onSettled: () => setTogglingFn(null),
  });

  // Počet vypnutých funkcí (enabled === false) pro summary.
  const disabledCount = functions.filter((f) => f.enabled === false).length;

  // Lookup mapa name → probe entry pro per-row badge.
  const probeMap = new Map<string, { running: boolean; status: number | null; detail?: string }>();
  if (probe) {
    for (const res of probe.results) {
      probeMap.set(res.name, { running: res.running, status: res.status, detail: res.detail });
    }
  }

  const hasPendingValues = Object.values(values).some((v) => v !== '');

  return (
    <div className='space-y-4'>
      {/* Stav načítání / chyba */}
      {info.isLoading && (
        <div className='text-xs text-gray-500 flex items-center gap-2'>
          <Loader2 size={12} className='animate-spin' /> {t('supabase.loadingFunctions')}
        </div>
      )}
      {info.isError && (
        <div className='p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-[11px] text-red-300'>
          {t('supabase.loadFunctionsFailed', { error: (info.error as Error)?.message })}
        </div>
      )}
      {data && !data.ok && (
        <div className='p-2 rounded-lg bg-red-500/10 border border-red-500/30 text-[11px] text-red-300'>
          {data.error ?? t('supabase.functionsInfoError')}
        </div>
      )}

      {data?.ok && (
        <>
          {/* Funkce summary + collapsible list */}
          <div className='p-3 rounded-xl bg-gray-800/50 border border-gray-700/50'>
            <div className='flex items-center gap-2 text-xs text-gray-400 flex-wrap'>
              <Zap size={13} className='text-gray-400' />
              <span className='font-medium'>{t('supabase.edgeFunctionsCount', { count: data.functions_count ?? functions.length })}</span>
              {disabledCount > 0 && (
                <span className='text-[10px] px-1.5 py-0.5 rounded bg-gray-700/40 text-gray-400 border border-gray-600/40'>
                  {t('supabase.disabledCount', { count: disabledCount })}
                </span>
              )}
              {data.edge_running === false && (
                <span className='text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300'>
                  {t('supabase.containerNotRunning')}
                </span>
              )}
              {data.edge_running && (
                <span className='text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-300'>
                  {t('supabase.running')}
                </span>
              )}
              {/* Probe summary — {running}/{total} běží (zelená/amber/červená) */}
              {probe && (
                <span
                  className={`text-[10px] px-1.5 py-0.5 rounded border font-medium ${
                    probe.down_count > 0
                      ? 'bg-red-500/15 text-red-300 border-red-500/30'
                      : 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
                  }`}
                  title={probe.down_count > 0 ? t('supabase.downListTitle', { names: probe.down.join(', ') }) : t('supabase.allFunctionsRunningTitle')}
                >
                  {probe.down_count > 0
                    ? t('supabase.probeSummaryDown', { down: probe.down_count, running: probe.running_count, total: probe.total })
                    : t('supabase.probeSummaryRunning', { running: probe.running_count, total: probe.total })}
                </span>
              )}
              <div className='ml-auto'>
                <Button
                  variant='secondary'
                  size='sm'
                  loading={probeFunctions.isPending}
                  disabled={probeFunctions.isPending}
                  icon={<RefreshCw size={12} />}
                  onClick={() => probeFunctions.mutate()}
                >
                  {probeFunctions.isPending ? t('supabase.testing') : t('supabase.testFunctionRun')}
                </Button>
              </div>
            </div>
            {functions.length > 0 && (
              <details className='mt-2'>
                <summary className='text-[11px] text-gray-500 cursor-pointer hover:text-gray-300 select-none'>
                  {t('supabase.showFunctionsAndKeys')}
                </summary>
                <div className='mt-2 space-y-2'>
                  {functions.map((fn) => {
                    const fnProbe = probeMap.get(fn.name);
                    const fnMissing = fn.missing_keys ?? [];
                    const fnEnabled = fn.enabled !== false;
                    const fnBusy = togglingFn === fn.name;
                    return (
                      <div key={fn.name} className={`text-[11px] ${fnEnabled ? '' : 'opacity-60'}`}>
                        <div className='flex items-center gap-2 flex-wrap'>
                          <code className='font-mono text-gray-300'>{fn.name}</code>
                          {/* Vypnuto badge — dir přejmenován na <name>.disabled, router 404 */}
                          {!fnEnabled && (
                            <span className='text-[10px] px-1.5 py-0.5 rounded bg-gray-700/40 text-gray-400 border border-gray-600/40'>
                              {t('supabase.disabled')}
                            </span>
                          )}
                          {/* Enable/disable toggle */}
                          <button
                            type='button'
                            disabled={fnBusy}
                            onClick={() => toggleFunction.mutate({ name: fn.name, enabled: !fnEnabled })}
                            title={fnEnabled ? t('supabase.disableFunction') : t('supabase.enableFunction')}
                            className={`text-[10px] px-1.5 py-0.5 rounded border inline-flex items-center gap-1 transition-colors ${
                              fnEnabled
                                ? 'bg-emerald-500/15 text-emerald-400 border-emerald-500/20 hover:bg-emerald-500/25'
                                : 'bg-gray-800 text-gray-400 border-gray-700 hover:bg-gray-700'
                            } ${fnBusy ? 'opacity-50 cursor-not-allowed' : ''}`}
                          >
                            {fnBusy && <Loader2 size={10} className='animate-spin' />}
                            {fnEnabled ? t('supabase.enabled') : t('supabase.disabled')}
                          </button>
                          {/* Runtime badge — netestováno / běží / neběží (+status v tooltipu) */}
                          {!fnProbe
                            ? <span className='text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-500 border border-gray-700' title={t('supabase.notTested')}>—</span>
                            : fnProbe.running
                            ? (
                              <span
                                className='text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'
                                title={fnProbe.status !== null ? `HTTP ${fnProbe.status}` : t('supabase.running')}
                              >
                                {t('supabase.running')}
                              </span>
                            )
                            : (
                              <span
                                className='text-[10px] px-1.5 py-0.5 rounded bg-red-500/15 text-red-300 border border-red-500/30'
                                title={`${t('supabase.notRunning')}${fnProbe.status !== null ? ` — HTTP ${fnProbe.status}` : ''}${fnProbe.detail ? ` (${fnProbe.detail})` : ''}`}
                              >
                                {t('supabase.notRunning')}{fnProbe.status !== null ? ` ${fnProbe.status}` : ''}
                              </span>
                            )}
                          {/* Missing keys badge — z functions_info (automatic) */}
                          {fnMissing.length === 0
                            ? <span className='text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'>{t('supabase.keysOk')}</span>
                            : (
                              <span
                                className='text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/20'
                                title={t('supabase.missingTitle', { names: fnMissing.join(', ') })}
                              >
                                {t('supabase.missingKeys', { names: fnMissing.join(', ') })}
                              </span>
                            )}
                        </div>
                        <div className='flex flex-wrap gap-1 mt-1'>
                          {fn.env_keys.length === 0
                            ? <span className='text-gray-600'>{t('supabase.noSecrets')}</span>
                            : fn.env_keys.map((k) => (
                              <span
                                key={k}
                                className='text-[10px] px-1.5 py-0.5 rounded bg-gray-900 border border-gray-700 font-mono text-gray-400'
                              >
                                {k}
                              </span>
                            ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </details>
            )}
          </div>

          {/* Secrets table — řádek per referenced_key */}
          <div className='p-3 rounded-xl bg-gray-800/50 border border-gray-700/50 space-y-3'>
            <p className='text-xs text-gray-500 font-medium flex items-center gap-2'>
              <Lock size={12} className='text-gray-400' />
              Secrets
            </p>
            <p className='text-[11px] text-gray-500'>
              {t('supabase.secretsWriteOnlyHint')}
            </p>

            {rowKeys.length === 0
              ? (
                <p className='text-[11px] text-gray-600'>
                  {t('supabase.noFunctionsReadSecrets')}
                </p>
              )
              : (
                <div className='space-y-1.5'>
                  {/* hlavička */}
                  <div className='grid grid-cols-[1fr_auto_auto_1.4fr] gap-2 items-center text-[10px] text-gray-600 px-1'>
                    <span>{t('supabase.colKey')}</span>
                    <span>{t('supabase.colState')}</span>
                    <span>{t('supabase.colFunctions')}</span>
                    <span>{t('supabase.colNewValue')}</span>
                  </div>
                  {rowKeys.map((key) => {
                    const isSet = setKeys.has(key);
                    const isMissing = missingKeys.has(key);
                    const fns = keyToFns[key] ?? [];
                    return (
                      <div
                        key={key}
                        className='grid grid-cols-[1fr_auto_auto_1.4fr] gap-2 items-center'
                      >
                        <code className='font-mono text-xs text-gray-300 truncate' title={key}>
                          {key}
                        </code>
                        {isSet
                          ? (
                            <span className='text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/15 text-emerald-400 border border-emerald-500/20'>
                              {t('supabase.set')}
                            </span>
                          )
                          : isMissing
                          ? (
                            <span className='text-[10px] px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-300 border border-amber-500/20'>
                              {t('supabase.missing')}
                            </span>
                          )
                          : <span className='text-[10px] text-gray-600'>—</span>}
                        <span
                          className='text-[10px] text-gray-500 text-center'
                          title={fns.length ? fns.join(', ') : t('supabase.noFunction')}
                        >
                          {fns.length}×
                        </span>
                        <input
                          type='password'
                          placeholder={t('supabase.newValuePlaceholder')}
                          className='bg-gray-950 border border-gray-800 rounded-lg px-2 py-1 text-xs text-white'
                          value={values[key] ?? ''}
                          onChange={(e) =>
                            setValues((v) => ({ ...v, [key]: e.target.value }))}
                        />
                      </div>
                    );
                  })}
                </div>
              )}

            {/* + vlastní klíč */}
            <div>
              <Button
                variant='secondary'
                size='sm'
                icon={<Plus size={14} />}
                onClick={async () => {
                  const k = (await themedPrompt(t('supabase.newSecretKeyPrompt')))?.trim();
                  if (k && !rowKeys.includes(k)) setCustomKeys((c) => [...c, k]);
                }}
              >
                {t('supabase.customKey')}
              </Button>
            </div>

            {extraSetKeys.length > 0 && (
              <p className='text-[11px] text-gray-500'>
                {t('supabase.extraSetKeys', { names: extraSetKeys.join(', ') })}
              </p>
            )}

            <div className='flex justify-end'>
              <Button
                variant='primary'
                size='sm'
                loading={saveSecrets.isPending}
                disabled={saveSecrets.isPending || !hasPendingValues}
                onClick={() => saveSecrets.mutate()}
              >
                {t('supabase.saveSecrets')}
              </Button>
            </div>
          </div>
        </>
      )}

      {/* Re-deploy funkcí (sekundární) */}
      <div className='p-3 rounded-xl bg-gray-800/30 border border-gray-700/30 space-y-2'>
        <h4 className='text-xs font-medium text-gray-400 flex items-center gap-2'>
          <Zap size={13} className='text-gray-400' />
          {t('supabase.redeployFunctions')}
        </h4>
        <div className='space-y-1'>
          <Label>{t('supabase.sourceDir')}</Label>
          <input
            type='text'
            placeholder='/opt/apphost/<fe>/src/supabase/functions'
            className='w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white font-mono'
            value={sourceDir}
            onChange={(e) => setSourceDir(e.target.value)}
          />
        </div>
        <div className='flex justify-end'>
          <Button
            variant='secondary'
            size='sm'
            loading={deployFunctions.isPending}
            disabled={!sourceDir || deployFunctions.isPending}
            onClick={() => deployFunctions.mutate()}
          >
            {t('supabase.redeployFunctions')}
          </Button>
        </div>
      </div>
    </div>
  );
}

function EditModal(
  { inst: initialInst, onClose }: { inst: SupabaseInstance; onClose: () => void },
) {
  const { t } = useTranslation();
  const qc = useQueryClient();

  // useLiveProp: drží `inst` napojený na query cache. Po `invalidateQueries`
  // se sem propíší čerstvá data a toggle/detail se vizuálně updatnou.
  // Bez toho byl modal vázán na snapshot propu → toggle se neměnil.
  // Viz wiki: ui-pattern-live-prop
  const inst = useLiveProp<SupabaseListResult, SupabaseInstance>(initialInst, {
    queryKey: ['supabase-instances', scopeKey()],
    select: (data) => data.instances?.find((i) => i.name === initialInst.name),
  });

  const [dbSchemas, setDbSchemas] = useState(inst.db_schemas ?? '');
  const originalSchemas = inst.db_schemas ?? '';

  const { data: creds, isLoading: credsLoading } = useQuery<FullCreds>({
    queryKey: ['supa-creds-full', inst.name],
    queryFn: () =>
      callMcp('supabase.credentials', { instance: inst.name }) as Promise<FullCreds>,
    enabled: !inst.legacy,
  });

  // Publish/unpublish přes MCP (supabase.publish / supabase.unpublish)
  const togglePublic = useMutation({
    mutationFn: async (
      { target, enable, access }: {
        target: 'api' | 'studio';
        enable: boolean;
        access?: 'shadow' | 'public';
      },
    ) => {
      // Instance patřící projektu se volá `PRJ-….supabase.publish`, ne
      // `supabase.publish` — jinak platforma odmítne s project_qualified_tool_required
      // a přepnutí přístupu ze stránky vůbec neprojde (Filip, dev 8. 9. 2026).
      const { tool, args } = instanceToolCall(
        'supabase',
        enable ? 'publish' : 'unpublish',
        inst.name,
        inst.project,
      );
      // Zapnutí Studia bez další volby jde do shadow modu — venkovní URL
      // existuje, ale projdou jen adresy, ze kterých je někdo přihlášený.
      const accessArg = enable && target === 'studio' ? { access: access ?? 'shadow' } : {};
      return callMcp(tool, { ...args, target, ...accessArg }) as Promise<
        { ok: boolean; error?: string }
      >;
    },
    onSuccess: (r: { ok: boolean; error?: string }) => {
      if (!r.ok) {
        toast.error(r.error ?? t('supabase.changeFailed'));
        return;
      }
      toast.success(t('supabase.stateUpdated'));
      qc.invalidateQueries({ queryKey: ['supabase-instances', scopeKey()] });
    },
    onError: (e: Error) => toast.error(e.message),
  });
  // Backwards compat aliases

  const reconfigure = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      callMcp<{ ok: boolean; error?: string }>('supabase.reconfigure', {
        name: inst.name,
        ...body,
      }),
    onSuccess: (r: { ok: boolean; error?: string }) => {
      if (!r.ok) {
        toast.error(r.error ?? t('supabase.saveFailed'));
        return;
      }
      toast.success(t('supabase.settingsSaved'));
      qc.invalidateQueries({ queryKey: ['supabase-instances', scopeKey()] });
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const schemasChanged = dbSchemas !== originalSchemas && dbSchemas.trim() !== '';

  return (
    <BodyOverlay>
    <div className='fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50'>
      {/* max-w-3xl (= 768px) místo max-w-lg (= 512px) — Filip 2026-06-03:
          "nastavení supabase musí být širší, takhle je nepřehledné, jako
          dlouhá nudle". */}
      <div className='bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto'>
        <div className='flex items-center justify-between mb-5'>
          <h3 className='text-lg font-semibold text-white'>{t('supabase.settingsTitle', { name: inst.name })}</h3>
          <button
            onClick={onClose}
            className='p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800'
          >
            <X size={16} />
          </button>
        </div>

        {/* Legacy instance nemá taby — jen krátká hláška */}
        {inst.legacy
          ? (
            <p className='text-sm text-gray-500 mb-5'>
              {t('supabase.legacyNoSettings')}
            </p>
          )
          : (
            <Tabs defaultTab='pristup'>
              <TabList>
                <Tab id='pristup' icon={<Globe size={14} />}>{t('supabase.tabAccess')}</Tab>
                <Tab id='schema' icon={<Layers size={14} />}>{t('supabase.tabSchema')}</Tab>
                <Tab id='edge' icon={<Zap size={14} />}>{t('supabase.tabEdge')}</Tab>
                <Tab id='zalohy' icon={<Database size={14} />}>{t('supabase.tabBackups')}</Tab>
              </TabList>

              {/* ── Přístup ── */}
              <TabPanel id='pristup'>
        {/* Adresy */}
        {!inst.legacy && (
          <div className='mb-5 p-3 rounded-xl bg-gray-800/50 border border-gray-700/50 space-y-2'>
            <p className='text-xs text-gray-500 font-medium mb-2'>{t('supabase.addresses')}</p>
            {/* Scope info banner — 127.0.0.1 URLs jsou jen z host loopback,
                FE v separate docker network k nim nedosáhne bez NPM publish
                nebo orch_connect_internal_backend. Viz Bug #2/#3 fix.        */}
            {(inst.api_scope === 'internal' || inst.studio_scope === 'internal') && (
              // Sdílený Alert, ne vlastní amber div. Barevný signál nese accent
              // a ikona, text je inkoust podle motivu — pastelové `text-amber-300/70`
              // na amber podkladu bylo oranžové na žlutém a nešlo přečíst.
              <Alert variant='warning' className='mb-3 p-2 text-[11px]'>
                <span className='font-bold'>{t('supabase.internalScopeLabel')}</span>
                {' '}{t('supabase.internalScopeHint')}
              </Alert>
            )}
            {/* Adresy — 3 vrstvy. Filip 2026-06-03: "není rozlišená veřejná
                a privátní adresa. https dělá až nginx ne?"
                Vrstvy v UI (z supabase_list):
                  raw_*  = http://127.0.0.1:port — funguje vždy z mc01 hosta
                  auth_* = https://host:auth_port — ACM nginx wrapper s SSO,
                           jen pokud auth_nginx_deployed=true
                  public_* = NPM published URL — jen pokud actual=true
                Otevřít Studio preferuje: public > auth > raw. */}
            <div className='space-y-1 mb-2'>
              {/* RAW kong/studio na 127.0.0.1 — vždy zobrazené, vysvětlené */}
              {inst.raw_api && (
                <UrlRow label={t('supabase.urlRawApi')} url={inst.raw_api} />
              )}
              {inst.raw_studio && (
                <UrlRow label={t('supabase.urlRawStudio')} url={inst.raw_studio} />
              )}
              {/* AUTH wrapper — ACM nginx s auth_request, jen pokud nasazený */}
              {inst.auth_api && (
                <UrlRow label={t('supabase.urlAuthApi')} url={inst.auth_api} />
              )}
              {inst.auth_studio && (
                <UrlRow label={t('supabase.urlAuthStudio')} url={inst.auth_studio} />
              )}
              {/* Nginx not deployed warning + regen button */}
              {inst.auth_nginx_deployed === false && (
                <div className='mt-2 p-2 rounded-lg bg-orange-500/10 border border-orange-500/30 text-[11px] text-orange-200'>
                  <span className='font-bold'>{t('supabase.nginxWrapperMissing')}</span>
                  <span className='text-orange-300/70'>
                    {' '}{t('supabase.nginxWrapperMissingHint', { name: inst.name })}{' '}
                    <code className='font-mono'>supabase_regenerate_nginx_auth</code> tool.
                  </span>
                </div>
              )}
              {/* Otevřít Studio — jen adresou, která vede PŘES PŘIHLÁŠENÍ, a jen
                  takovou, na kterou návštěvník odtud dosáhne.

                  Filip 8. 9. 2026: „v hlavním ovládacím widgetu na supabase je
                  teď proklik s lokální adresou, ale je potřeba to primárně volat
                  přes SSO ne? A tam by se měla pak doplnit ta adresa, presentování
                  v jakém je režimu."

                  Proto `raw_studio` (http://127.0.0.1:port) v nabídce NENÍ: obchází
                  ACM nginx wrapper, tedy i přihlášení a shadow mode. Zbývá
                  publikovaná URL a wrapper na vnitřní adrese uzlu — obě projdou
                  auth podotázkou. Vedle odkazu je adresa a režim, ať je vidět,
                  pro koho ten odkaz platí. */}
              {(inst.status === 'healthy' || inst.status === 'running')
                ? (() => {
                  const pick = pickReachable([
                    { kind: 'public' as const, url: inst.public_studio },
                    { kind: 'auth' as const, url: inst.auth_studio },
                  ]);
                  const studioHref = pick.chosen?.url ?? null;
                  const access = inst.studio_access ?? 'shadow';
                  // Režim omezuje jen publikovanou adresu; na wrapper na vnitřní
                  // adrese uzlu se chodí zevnitř, tam neomezuje nic.
                  const showAccess = pick.chosen?.kind === 'public';
                  const skippedNote = pick.skipped.length > 0
                    ? t(
                      'supabase.studioOnlyInternal',
                      'Studio má jen vnitřní adresu ({{urls}}) — odsud se na ni nedostanete. Otevřete ACM z vnitřní sítě nebo instanci publikujte.',
                      { urls: t('supabase.urlAuthStudio') },
                    )
                    : null;
                  return studioHref
                    ? (
                      <div className='mt-1 flex flex-wrap items-center gap-2'>
                        <a
                          href={studioHref}
                          target='_blank'
                          rel='noopener'
                          className='inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-blue-600/20 border border-blue-500/30 text-blue-400 text-xs font-medium hover:bg-blue-600/30 transition-colors'
                        >
                          <ExternalLink size={12} />
                          {pick.chosen?.kind === 'public'
                            ? t('supabase.openStudioPublic')
                            : t('supabase.openStudioAuth')}
                        </a>
                        <span className='text-[10px] text-gray-500 font-mono break-all'>
                          {studioHref}
                        </span>
                        {showAccess && (
                          <Badge variant={access === 'public' ? 'success' : 'warning'}>
                            {access === 'public'
                              ? t('supabase.studioAccessPublic')
                              : t('supabase.studioAccessShadow')}
                          </Badge>
                        )}
                      </div>
                    )
                    : (
                      <span
                        className='inline-flex items-start gap-2 px-3 py-1.5 rounded-lg bg-gray-800/50 border border-gray-700/30 text-gray-400 text-xs mt-1 max-w-xl'
                        title={skippedNote ?? undefined}
                      >
                        {skippedNote ?? t('supabase.studioUrlUnavailable')}
                      </span>
                    );
                })()
                : (
                  <span className='inline-flex items-center gap-2 px-3 py-1.5 rounded-lg bg-gray-800/50 border border-gray-700/30 text-gray-500 text-xs mt-1'>
                    {t('supabase.studioUnavailableStopped')}
                  </span>
                )}
              {creds?.db_url && <UrlRow label={t('supabase.postgres')} url={creds.db_url} />}
            </div>
            {inst.api_public_enabled && inst.public_api && (
              <div className='space-y-1'>
                <UrlRow label={t('supabase.urlPublicApi')} url={inst.public_api} isPublic />
                {inst.api_cert && (
                  <div className='flex items-center gap-2 pl-4'>
                    <CertBadge cert={inst.api_cert} label='API' />
                    <span className='text-xs text-gray-500'>
                      {t('supabase.validUntil', {
                        date: new Date(inst.api_cert.expires_on).toLocaleDateString('cs-CZ', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        }),
                      })}
                    </span>
                  </div>
                )}
              </div>
            )}
            {inst.studio_public_enabled && inst.public_studio && (
              <div className='space-y-1'>
                <UrlRow label={t('supabase.urlPublicStudio')} url={inst.public_studio} isPublic />
                {inst.studio_cert && (
                  <div className='flex items-center gap-2 pl-4'>
                    <CertBadge cert={inst.studio_cert} label='Studio' />
                    <span className='text-xs text-gray-500'>
                      {t('supabase.validUntil', {
                        date: new Date(inst.studio_cert.expires_on).toLocaleDateString('cs-CZ', {
                          day: 'numeric',
                          month: 'long',
                          year: 'numeric',
                        }),
                      })}
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Publish/Unpublish — AsyncStatusToggle */}
        {/* Viz wiki: ui-pattern-async-toggle. Desired = *_public_enabled (DB), */}
        {/* Actual = *_public (živý NPM host). Syncing se ukazuje dokud nejsou v souladu. */}
        {!inst.legacy && (
          <div className='mb-5 p-3 rounded-xl bg-gray-800/50 border border-gray-700/50'>
            <p className='text-xs text-gray-500 font-medium mb-3'>{t('supabase.publicAccess')}</p>
            <div className='space-y-4'>
              {/* API */}
              <div>
                <AsyncStatusToggle
                  value={inst.api_public_enabled}
                  actual={inst.api_public}
                  label='API'
                  renderIcon={(on) =>
                    on
                      ? <Globe size={13} className='text-emerald-400' />
                      : <Lock size={13} className='text-gray-500' />}
                  renderIdleBadge={(on) =>
                    on
                      ? (
                        <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400'>
                          {t('supabase.published')}
                        </span>
                      )
                      : null}
                  onToggle={(v) => togglePublic.mutateAsync({ target: 'api', enable: v })}
                  onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
                />
                {inst.url_hash && (
                  <div className='mt-1.5 ml-5'>
                    <span className='text-[10px] text-gray-500 font-mono'>
                      https://{inst.url_hash}.sup.services.maxcloud.cz
                    </span>
                  </div>
                )}
              </div>

              {/* Studio */}
              <div>
                <AsyncStatusToggle
                  value={inst.studio_public_enabled}
                  actual={inst.studio_public}
                  label='Studio'
                  renderIcon={(on) =>
                    on
                      ? <Globe size={13} className='text-emerald-400' />
                      : <Lock size={13} className='text-gray-500' />}
                  renderIdleBadge={(on) =>
                    on
                      ? (
                        <span className='text-[9px] px-1.5 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400'>
                          {t('supabase.published')}
                        </span>
                      )
                      : null}
                  onToggle={(v) => togglePublic.mutateAsync({ target: 'studio', enable: v })}
                  onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
                />
                {inst.url_hash && (
                  <div className='mt-1.5 ml-5'>
                    <span className='text-[10px] text-gray-500 font-mono'>
                      https://{inst.url_hash}.ssup.services.maxcloud.cz
                    </span>
                  </div>
                )}
                {/* Režim přístupu — jen u publikovaného Studia. Filip 2026-09-08:
                    „takže tam jsou možnosti povolit veřejně a povolit v shadow
                    modu, aby byla jako výchozí možnost shadow?" Ano: zapnutí
                    jde do shadow a tohle je přepnutí na veřejné a zpět. */}
                {!inst.studio_public_enabled && (
                  <div className='mt-1.5 ml-5 text-[10px] text-gray-500'>
                    {t('supabase.studioAccessOnEnable')}
                  </div>
                )}
                {inst.studio_public_enabled && (
                  <div className='mt-2 ml-5'>
                    <div className='text-[10px] text-gray-500 mb-1'>
                      {t('supabase.studioAccessTitle')}
                    </div>
                    <div className='inline-flex rounded-md overflow-hidden border border-white/10'>
                      {(['shadow', 'public'] as const).map((mode) => {
                        const active = (inst.studio_access ?? 'shadow') === mode;
                        return (
                          <button
                            key={mode}
                            type='button'
                            disabled={togglePublic.isPending}
                            onClick={() =>
                              togglePublic.mutate({
                                target: 'studio',
                                enable: true,
                                access: mode,
                              })}
                            className={`px-2 py-1 text-[10px] transition-colors disabled:opacity-50 ${
                              active
                                ? mode === 'shadow'
                                  ? 'bg-amber-500/20 border border-amber-500/40 text-gray-100'
                                  : 'bg-emerald-500/20 border border-emerald-500/40 text-gray-100'
                                : 'text-gray-400 hover:bg-white/5'
                            }`}
                          >
                            {mode === 'shadow'
                              ? t('supabase.studioAccessShadow')
                              : t('supabase.studioAccessPublic')}
                          </button>
                        );
                      })}
                    </div>
                    <div className='text-[10px] text-gray-500 mt-1 max-w-md leading-snug'>
                      {(inst.studio_access ?? 'shadow') === 'public'
                        ? t('supabase.studioAccessPublicHint')
                        : t('supabase.studioAccessShadowHint')}
                    </div>
                  </div>
                )}
                {/* SSO toggle pro Studio (jen když published).
                    Architektura: viz wiki `sso-publish-architecture`.
                    Auth je VŽDY povinná (nginx auth_request → /authz validuje
                    apikey | session | KC token). SSO toggle určuje JEN
                    primární cestu z venku (KC redirect vs login form). */}
                {inst.studio_public && inst.studio_domain && (
                  <div className='mt-2 ml-5 px-3 py-2 rounded-lg bg-gray-900/50 border border-gray-800'>
                    <AsyncStatusToggle
                      value={inst.studio_sso_enabled ?? false}
                      actual={inst.studio_sso_registered ?? false}
                      label={
                        <div className='flex flex-col'>
                          <span className='text-[11px] text-gray-300'>
                            {t('supabase.keycloakSsoExternal')}
                            {inst.studio_sso_client_id && (
                              <span className='font-mono text-gray-500 ml-1'>
                                ({inst.studio_sso_client_id})
                              </span>
                            )}
                          </span>
                          <span className='text-[10px] text-gray-600'>
                            {t('supabase.loginFormFallbackHint')}
                          </span>
                        </div>
                      }
                      renderIcon={(on) => (
                        <Shield size={12} className={on ? 'text-blue-400' : 'text-gray-600'} />
                      )}
                      onToggle={(v) =>
                        callMcp('network.set_sso', { domain: inst.studio_domain, enabled: v })}
                      onError={(e) => toast.error(e instanceof Error ? e.message : String(e))}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Credentials */}
        {!inst.legacy && (
          <div className='mb-5 space-y-2'>
            <p className='text-xs text-gray-500 font-medium'>{t('supabase.keysAndPasswords')}</p>
            {credsLoading && <p className='text-xs text-gray-600'>{t('supabase.loading')}</p>}
            {creds?.ok && (
              <>
                {creds.api_url && <SecretField label='Supabase URL' value={creds.api_url} />}
                <SecretField label='Anon Key' value={creds.anon_key} />
                <SecretField label='Service Role Key' value={creds.service_role_key} />
                <SecretField label='JWT Secret' value={creds.jwt_secret} />
                {creds.db_url && (
                  <>
                    <SecretField label='Database URL' value={creds.db_url} />
                    <button
                      onClick={() => {
                        // Filip 2026-06-03: "kopírovat se má public a použitelná
                        // private, ne ta localhost. Pokud public není, tak jen
                        // ta použitelná private."
                        //
                        // Priority chain:
                        //   1. public_* (= NPM published URL)
                        //   2. auth_* (= ACM nginx wrapper přes host IP)
                        //   3. raw_* (= 127.0.0.1 — fallback, useless externě)
                        //
                        // Localhost URL (= 127.0.0.1) v clipboardu jen pokud
                        // žádná lepší alternativa NEEXISTUJE, jinak skipnem.
                        const apiUrl = inst.public_api ?? inst.auth_api ?? null;
                        const studioUrl = inst.public_studio ?? inst.auth_studio ?? null;
                        const apiFallbackRaw = !apiUrl ? inst.raw_api : null;
                        const studioFallbackRaw = !studioUrl ? inst.raw_studio : null;

                        const lines: string[] = [];
                        // Postgres connection string (URL formát co se vleze do client config).
                        if (creds.db_url) {
                          lines.push(`Database URL: ${creds.db_url}`);
                        }
                        lines.push(`Anon Key: ${creds.anon_key}`);
                        lines.push(`Service Role Key: ${creds.service_role_key}`);

                        if (apiUrl) {
                          lines.push(`API URL${inst.public_api ? ' (public)' : ' (auth wrapper)'}: ${apiUrl}`);
                        } else if (apiFallbackRaw) {
                          lines.push(`${t('supabase.apiUrlRawLine')}: ${apiFallbackRaw}`);
                        }
                        if (studioUrl) {
                          lines.push(`Studio${inst.public_studio ? ' (public)' : ' (auth wrapper)'}: ${studioUrl}`);
                        } else if (studioFallbackRaw) {
                          lines.push(`${t('supabase.studioRawLine')}: ${studioFallbackRaw}`);
                        }

                        navigator.clipboard.writeText(lines.join('\n'));

                        // Upozorni pokud jediná dostupná URL je raw localhost
                        const onlyRaw = (!apiUrl && apiFallbackRaw) || (!studioUrl && studioFallbackRaw);
                        if (onlyRaw) {
                          toast.success(
                            t('supabase.connectionInfoCopiedRawWarn'),
                            { duration: 6000 },
                          );
                        } else {
                          toast.success(t('supabase.connectionInfoCopied'));
                        }
                      }}
                      className='w-full mt-2 px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-gray-300 text-xs font-medium hover:bg-gray-700 transition-colors flex items-center justify-center gap-2'
                    >
                      <Copy size={12} />
                      {t('supabase.copyConnectionInfo')}
                    </button>
                  </>
                )}
              </>
            )}
          </div>
        )}
              </TabPanel>

              {/* ── Schéma ── */}
              <TabPanel id='schema'>
        {/* DB schémata */}
        {!inst.legacy && (
          <div className='space-y-2 mb-6'>
            <p className='text-xs text-gray-500 font-medium'>{t('supabase.postgrestConfig')}</p>
            <div>
              <label className='text-xs text-gray-400 mb-1 block'>
                {t('supabase.dbSchemas')} <span className='text-gray-600'>{t('supabase.dbSchemasHint')}</span>
              </label>
              <input
                value={dbSchemas}
                onChange={(e) => setDbSchemas(e.target.value)}
                placeholder='public,storage,graphql_public'
                className='w-full px-3 py-2 rounded-lg bg-gray-800 border border-gray-700 text-white text-sm focus:outline-none focus:border-blue-500'
              />
              {schemasChanged && (
                <p className='text-xs text-amber-400 mt-1 flex items-center gap-1'>
                  <Loader2 size={10} className='hidden' />
                  {t('supabase.willRestartServices')}
                </p>
              )}
            </div>
          </div>
        )}
              </TabPanel>

              {/* ── Edge funkce ── (data-driven secrets, viz EdgeFunctionsPanel) */}
              <TabPanel id='edge'>
                <EdgeFunctionsPanel instanceName={inst.name} />
              </TabPanel>

              {/* ── Zálohy ── */}
              <TabPanel id='zalohy'>
        {
          /* Zálohy — přenosné backupy + rychlé lokální snapshoty. Auto pre-delete backup
            se vytváří v supabase_delete; tady jsou manuální akce a restore z existujících. */
        }
        {!inst.legacy && (
          <div className='mb-5 p-3 rounded-xl bg-gray-800/30 border border-gray-700/30'>
            <p className='text-xs text-gray-500 font-medium mb-2'>{t('supabase.backups')}</p>
            <BackupPanel module='supabase' instanceName={inst.name} containerPrefix={inst.name} />
          </div>
        )}
              </TabPanel>
            </Tabs>
          )}

        <div className='flex gap-3'>
          <button
            onClick={onClose}
            className='flex-1 py-2 rounded-xl bg-gray-800 text-gray-300 text-sm hover:bg-gray-700'
          >
            {t('supabase.close')}
          </button>
          {!inst.legacy && (
            <button
              onClick={() => reconfigure.mutate({ db_schemas: dbSchemas, restart: true })}
              disabled={!schemasChanged || reconfigure.isPending}
              className='flex-1 py-2 rounded-xl bg-blue-600 text-white text-sm hover:bg-blue-500 disabled:opacity-40 transition-opacity'
            >
              {reconfigure.isPending ? t('supabase.saving') : t('supabase.saveDbSchemas')}
            </button>
          )}
        </div>
      </div>
    </div>
    </BodyOverlay>
  );
}

function CreateModal({ onClose, onDeployed }: { onClose: () => void; onDeployed: () => void }) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [name, setName] = useState('');
  const [mode, setMode] = useState<'deploy' | 'provision'>('deploy');
  const [waitHealthy, setWaitHealthy] = useState(false);
  const [targetNode, setTargetNode] = useState(''); // '' = local ACM

  const slugOk = /^[a-z0-9-]+$/.test(name);

  const mut = useMutation({
    mutationFn: () => {
      const toolName = mode === 'provision' ? 'supabase.provision' : 'supabase.deploy';
      const args = mode === 'provision'
        ? { name, wait_healthy: waitHealthy }
        : { name, start: true };
      if (targetNode && targetNode !== '') {
        // Cross-node deploy přes CCP proxy
        return callMcp<{ ok: boolean; error?: string }>(
          'ccp.call_node',
          { node: targetNode, tool: toolName, args },
        );
      }
      return callMcp<{ ok: boolean; error?: string }>(toolName, args);
    },
    onSuccess: (r) => {
      if (!r.ok) {
        toast.error(r.error ?? t('supabase.failed'));
        return;
      }
      // Sync fáze vrátí rychle (~1s), deploy pokračuje na pozadí.
      // Instance se objeví v seznamu s statusem "nasazuje se…".
      toast.success(t('supabase.instanceDeployingBg', { name }));
      qc.invalidateQueries({ queryKey: ['supabase-instances', scopeKey()] });
      onDeployed();
      onClose();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const isProd = mode === 'provision';

  return (
    <BodyOverlay>
    <div className='fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50'>
      <div className='bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-md'>
        <div className='flex items-center justify-between mb-5'>
          <h3 className='text-lg font-semibold text-white'>{t('supabase.newInstance')}</h3>
          <button
            onClick={onClose}
            className='p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800'
          >
            <X size={16} />
          </button>
        </div>

        <div className='space-y-4 mb-6'>
          {/* Target node */}
          <NodeSelector
            value={targetNode}
            onChange={setTargetNode}
            label={t('supabase.targetNode')}
          />

          {/* Mode */}
          <div className='grid grid-cols-2 gap-2'>
            {([['deploy', t('supabase.modeQuickTest'), t('supabase.modeQuickTestDesc')], [
              'provision',
              t('supabase.modeProduction'),
              t('supabase.modeProductionDesc'),
            ]] as const).map(([m, label, desc]) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={`p-3 rounded-xl border text-left transition-colors ${
                  mode === m
                    ? 'border-blue-500 bg-blue-600/15'
                    : 'border-gray-700 bg-gray-800/60 hover:border-gray-600'
                }`}
              >
                <div
                  className={`text-sm font-medium ${
                    mode === m ? 'text-blue-300' : 'text-gray-300'
                  }`}
                >
                  {label}
                </div>
                <div className='text-xs text-gray-500 mt-0.5'>{desc}</div>
              </button>
            ))}
          </div>

          {/* Name */}
          <div>
            <label className='text-xs text-gray-400 mb-1 block'>
              {t('supabase.instanceName')} * <span className='text-gray-600'>{t('supabase.slugHint')}</span>
            </label>
            <input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder={t('supabase.mujProjekt', 'muj-projekt')}
              className={`w-full px-3 py-2 rounded-lg bg-gray-800 border text-white text-sm focus:outline-none ${
                name && !slugOk ? 'border-red-500' : 'border-gray-700 focus:border-blue-500'
              }`}
            />
            {name && !slugOk && (
              <p className='text-xs text-red-400 mt-1'>
                {t('supabase.slugError')}
              </p>
            )}
          </div>

          {/* Provision options */}
          {isProd && (
            <label className='flex items-center gap-3 cursor-pointer select-none'>
              <input
                type='checkbox'
                checked={waitHealthy}
                onChange={(e) => setWaitHealthy(e.target.checked)}
                className='accent-blue-500 w-4 h-4'
              />
              <div>
                <div className='text-sm text-gray-300'>{t('supabase.waitHealthy')}</div>
                <div className='text-xs text-gray-500'>
                  {t('supabase.waitHealthyDesc')}
                </div>
              </div>
            </label>
          )}

          {/* Publish info */}
          {isProd && (
            <div className='text-xs text-blue-400 bg-blue-500/10 border border-blue-500/20 rounded-lg px-3 py-2'>
              {t('supabase.publishInfoPre')} <strong>{t('supabase.publishInfoPrivate')}</strong>.{' '}
              {t('supabase.publishInfoMid')}{' '}
              <strong>Publish</strong> {t('supabase.publishInfoPost')}
            </div>
          )}

          {mut.isPending && (
            <div className='text-xs text-amber-400 bg-amber-400/10 border border-amber-500/20 rounded-lg px-3 py-2 flex items-center gap-2'>
              <Loader2 size={12} className='animate-spin' />
              {isProd ? t('supabase.initializingProd') : t('supabase.deploying')}{' '}
              {t('supabase.dockerPullHint')}
            </div>
          )}
        </div>

        <div className='flex gap-3'>
          <button
            onClick={onClose}
            className='flex-1 py-2 rounded-xl bg-gray-800 text-gray-300 text-sm hover:bg-gray-700'
          >
            {t('supabase.cancel')}
          </button>
          <button
            onClick={() => mut.mutate()}
            disabled={!name || !slugOk || mut.isPending}
            className={`flex-1 py-2 rounded-xl text-white text-sm disabled:opacity-50 ${
              isProd ? 'bg-green-700 hover:bg-green-600' : 'bg-blue-600 hover:bg-blue-500'
            }`}
          >
            {mut.isPending ? t('supabase.inProgress') : isProd ? 'Provision' : t('supabase.deploy')}
          </button>
        </div>
      </div>
    </div>
    </BodyOverlay>
  );
}

interface DockerContainer {
  id: string;
  name: string;
  state: string;
  status: string;
  image: string;
  ports: string;
  created: string;
}

function DeleteConfirmModal(
  { name, onConfirm, onClose }: { name: string; onConfirm: () => void; onClose: () => void },
) {
  const { t } = useTranslation();
  return (
    <BodyOverlay>
    <div className='fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-[60]'>
      <div className='bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-sm'>
        <div className='flex items-center gap-3 mb-4'>
          <div className='p-2 rounded-xl bg-red-500/10 border border-red-500/20'>
            <Trash2 size={18} className='text-red-400' />
          </div>
          <h3 className='text-base font-semibold text-white'>{t('supabase.deleteInstanceTitle')}</h3>
        </div>
        <p className='text-sm text-gray-400 mb-1'>
          {t('supabase.deleteInstancePre')}{' '}
          <code className='text-white font-mono bg-gray-800 px-1.5 py-0.5 rounded'>{name}</code>
          {' '}
          {t('supabase.deleteInstancePost')}
        </p>
        <p className='text-xs text-red-400 mb-5'>{t('supabase.irreversible')}</p>
        <div className='flex gap-3'>
          <Button variant='secondary' size='sm' className='flex-1' onClick={onClose}>{t('supabase.cancel')}</Button>
          <Button variant='danger' size='sm' className='flex-1' onClick={onConfirm}>{t('supabase.delete')}</Button>
        </div>
      </div>
    </div>
    </BodyOverlay>
  );
}

function InstancesTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [searchParams] = useSearchParams();
  const [showCreate, setShowCreate] = useState(false);
  const [editInst, setEditInst] = useState<SupabaseInstance | null>(null);
  const [fastRefresh, setFastRefresh] = useState(false);
  const [pendingAction, setPendingAction] = useState<Record<string, string>>({});
  const [expandedInst, setExpandedInst] = useState<string | null>(null);
  const [instFilter, setInstFilter] = useState<string>('all');
  const [deleteConfirm, setDeleteConfirm] = useState<string | null>(null);
  const [logViewer, setLogViewer] = useState<string | null>(null);

  const { data, isLoading, refetch } = useQuery<SupabaseListResult>({
    queryKey: ['supabase-instances', scopeKey()],
    queryFn: () => callMcp('supabase.list', {}),
    refetchInterval: fastRefresh ? 3_000 : 30_000,
  });

  // Docker overlay — docker.container_list (filtr per instance v renderu)
  const { data: containersData } = useQuery<DockerContainer[]>({
    queryKey: ['containers', scopeKey()],
    queryFn: async () => {
      const r = await callMcp<{ ok: boolean; total: number; containers?: DockerContainer[] }>(
        'docker.container_list',
        { all: true },
        { silent: true },
      );
      return r.containers ?? [];
    },
    staleTime: 10_000,
    refetchInterval: 15_000,
  });

  const manage = useMutation({
    mutationFn: ({ name, action }: { name: string; action: string }) => {
      setPendingAction((p) => ({ ...p, [name]: action }));
      return callMcp<{ ok: boolean; error?: string }>('supabase.manage', { name, action });
    },
    onSuccess: (r, { name, action }) => {
      setPendingAction((p) => {
        const n = { ...p };
        delete n[name];
        return n;
      });
      if (!r.ok) {
        toast.error(r.error ?? t('supabase.actionFailed', { action }));
        return;
      }
      toast.success(t('supabase.actionOk', { action }));
      qc.invalidateQueries({ queryKey: ['supabase-instances', scopeKey()] });
    },
    onError: (_e, { name }) => {
      setPendingAction((p) => {
        const n = { ...p };
        delete n[name];
        return n;
      });
      toast.error((_e as Error).message);
    },
  });

  const del = useMutation({
    mutationFn: (name: string) =>
      callMcp<{ ok: boolean; error?: string }>('supabase.delete', { name, remove_volumes: false }),
    onSuccess: (r, name) => {
      if (!r.ok) {
        toast.error(r.error ?? t('supabase.deleteFailed'));
        return;
      }
      // Sync fáze vrací hned, delete pokračuje na pozadí se status='deleting'.
      toast.success(t('supabase.instanceDeletingBg', { name }));
      qc.invalidateQueries({ queryKey: ['supabase-instances', scopeKey()] });
      // Aktivuj fast refresh (3s) aby user viděl průběh / zmizení instance
      setFastRefresh(true);
      setTimeout(() => setFastRefresh(false), 60_000);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const instances = (data?.instances ?? []).filter((i) => !i.legacy || i.total > 0);

  // Deep-link auto-open: /modules/supabase?instance=X otevře EditModal té instance.
  // useRef flag zajistí jednorázové auto-otevření — po zavření user-em už znovu
  // neotvíráme (jinak by se modal po každém refetchi znovu nasoukal).
  const autoOpenedRef = useRef(false);
  useEffect(() => {
    if (autoOpenedRef.current || editInst) return;
    const target = searchParams.get('instance');
    if (!target) return;
    const match = instances.find((i) => i.name === target || i.name === `${target}-backend`);
    if (match) {
      autoOpenedRef.current = true;
      setEditInst(match);
    }
  }, [instances, editInst, searchParams]);

  const cntHealthy = instances.filter((i) => i.status === 'healthy').length;
  const cntPublished = instances.filter((i) => i.api_public || i.studio_public).length;
  const cntStopped =
    instances.filter((i) =>
      i.status === 'stopped' || i.status === 'error' || i.status === 'partial'
    ).length;

  return (
    <div className='space-y-5'>
      <StatsGrid columns={4}>
        <StatCard
          icon={Database}
          label={t('supabase.statTotal')}
          value={instances.length}
          sub={t('supabase.statInstancesSub')}
          variant='info'
        />
        <StatCard
          icon={CheckCircle2}
          label={t('supabase.statHealthy')}
          value={cntHealthy}
          sub='healthy'
          variant='success'
        />
        <StatCard
          icon={Globe}
          label={t('supabase.statPublished')}
          value={cntPublished}
          sub={t('supabase.statPublishedSub')}
          variant='info'
        />
        <StatCard
          icon={XCircle}
          label={t('supabase.statStopped')}
          value={cntStopped}
          sub='stopped/err'
          variant='warning'
        />
      </StatsGrid>

      <Card>
        <CardHeader>
          <CardTitle icon={Database}>{t('supabase.instancesTitle')}</CardTitle>
          <div className='flex items-center gap-2'>
            {fastRefresh && (
              <span className='text-xs text-amber-400 flex items-center gap-1'>
                <RefreshCw size={11} className='animate-spin' /> {t('supabase.deployingShort')}
              </span>
            )}
            <Button variant='ghost' size='icon' onClick={() => refetch()} title={t('supabase.refresh')}>
              <RefreshCw size={14} />
            </Button>
            <Button
              variant='primary'
              size='sm'
              icon={<Plus size={13} />}
              onClick={() => setShowCreate(true)}
            >
              {t('supabase.addInstance')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading
            ? <div className='text-sm text-gray-500 py-4'>{t('supabase.loading')}</div>
            : instances.length === 0
            ? (
              <div className='text-center py-10'>
                <Database size={32} className='text-gray-700 mx-auto mb-3' />
                <p className='text-sm text-gray-500 mb-1'>{t('supabase.noInstances')}</p>
                <p className='text-xs text-gray-600 mb-4'>
                  {t('supabase.noInstancesHint')}
                </p>
                <Button
                  variant='primary'
                  size='md'
                  icon={<Plus size={14} />}
                  onClick={() => setShowCreate(true)}
                  className='mx-auto'
                >
                  {t('supabase.addFirstInstance')}
                </Button>
              </div>
            )
            : (
              <div className='space-y-2'>
                {/* Filter tabs */}
                <div className='flex gap-1 mb-3 border-b border-gray-800'>
                  {[
                    { id: 'all', label: t('supabase.filterAll', { count: instances.length }) },
                    {
                      id: 'healthy',
                      label: t('supabase.filterActive', {
                        count: instances.filter((i) => i.status === 'healthy' || i.status === 'running')
                          .length,
                      }),
                    },
                    {
                      id: 'stopped',
                      label: t('supabase.filterStopped', {
                        count: instances.filter((i) => i.status === 'stopped').length,
                      }),
                    },
                  ].map((f) => (
                    <button
                      key={f.id}
                      onClick={() => setInstFilter?.(f.id)}
                      className={`px-3 py-1.5 text-xs font-medium border-b-2 -mb-px transition-colors ${
                        (instFilter ?? 'all') === f.id
                          ? 'border-blue-400 text-blue-400'
                          : 'border-transparent text-gray-500 hover:text-white'
                      }`}
                    >
                      {f.label}
                    </button>
                  ))}
                </div>
                {instances.filter((i) => {
                  const f = instFilter ?? 'all';
                  if (f === 'all') return true;
                  if (f === 'healthy') return i.status === 'healthy' || i.status === 'running';
                  if (f === 'stopped') return i.status === 'stopped';
                  return true;
                }).map((inst) => {
                  const pending = pendingAction[inst.name];
                  const isActive = inst.status === 'healthy' || inst.status === 'running';
                  const isExpanded = expandedInst === inst.name;
                  // Reálné názvy: "<instance>-supabase-db", "<instance>-realtime-…" =
                  // začínají "<instance>-". Legacy konvence byla "supabase-<instance>-…".
                  const prefix = `${inst.name}-`;
                  const legacyPrefix = `supabase-${inst.name}-`;
                  const instContainers = (containersData ?? []).filter((c) => {
                    const n = c.name.replace(/^\//, '');
                    return n.startsWith(prefix) || n.startsWith(legacyPrefix) ||
                      n === inst.name || n === `supabase-${inst.name}`;
                  });
                  return (
                    <div
                      key={inst.name}
                      className='rounded-xl border border-gray-800 overflow-hidden'
                    >
                      {/* Instance card */}
                      <div className='flex items-center gap-4 p-4 hover:bg-gray-800/10 group'>
                        <div
                          className='flex-1 min-w-0 space-y-2 cursor-pointer'
                          onClick={() => setExpandedInst(isExpanded ? null : inst.name)}
                          title={t('supabase.clickToExpand')}
                        >
                          {/* Hlavička: název + node + status + stats */}
                          <div className='flex items-center gap-2 flex-wrap'>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setEditInst(inst);
                              }}
                              className='text-sm font-bold text-white hover:text-blue-400 cursor-pointer transition-colors'
                              title={t('supabase.openInstanceSettings')}
                            >
                              {inst.name}
                            </button>
                            {/* Node badge — interní/externí */}
                            {inst.private_api && (() => {
                              try {
                                const h = new URL(inst.private_api).hostname;
                                const local = h === location.hostname || h === '127.0.0.1' ||
                                  h === 'localhost';
                                return (
                                  <span
                                    className={`text-[10px] px-1.5 py-0.5 rounded ${
                                      local
                                        ? 'bg-blue-900/30 text-blue-400'
                                        : 'bg-amber-900/30 text-amber-400'
                                    }`}
                                  >
                                    {local ? t('supabase.local') : h}
                                  </span>
                                );
                              } catch {
                                return null;
                              }
                            })()}
                            <StatusBadge status={inst.status} />
                            <SyncBadge instanceName={inst.name} />
                            {inst.legacy && (
                              <span className='text-[10px] text-gray-600 bg-gray-800 px-1 py-0.5 rounded font-mono'>
                                legacy
                              </span>
                            )}
                            <span className='text-[10px] text-gray-500'>
                              {t('supabase.containersCount', { running: inst.running, total: inst.total })}
                            </span>
                            {(inst.memory_mb ?? 0) > 0 && (
                              <span className='text-[10px] text-gray-500'>
                                {(inst.memory_mb ?? 0) > 1024
                                  ? ((inst.memory_mb ?? 0) / 1024).toFixed(1) + ' GB'
                                  : (inst.memory_mb ?? 0) + ' MB'} RAM
                              </span>
                            )}
                            {inst.disk_mb !== undefined && (
                              <span className='text-[10px] text-gray-500'>
                                {(() => {
                                  const d = inst.disk_mb;
                                  if (d <= 0) return t('supabase.noDisk');
                                  return d >= 1024 ? (d / 1024).toFixed(1) + ' GB disk' : d + ' MB disk';
                                })()}
                              </span>
                            )}
                            {inst.db_schemas && (
                              <span className='text-[10px] text-gray-500'>
                                {inst.db_schemas.split(',').length} schemas
                              </span>
                            )}
                            {(inst.unhealthy ?? 0) > 0 && (
                              <span className='text-[10px] text-red-400'>
                                {inst.unhealthy} unhealthy
                              </span>
                            )}
                            {(() => {
                              const b = (inst as { backup?: { last_run_at: string | null; last_run_status: string | null; next_run_at: string | null; enabled: boolean } }).backup;
                              if (!b) return null;
                              const items = [];
                              if (b.last_run_at) {
                                const ok = b.last_run_status === 'ok' || b.last_run_status === 'success';
                                items.push(
                                  <span key='last' className={`text-[10px] ${ok ? 'text-gray-500' : 'text-amber-400'}`}>
                                    {t('supabase.backupOn', { date: new Date(b.last_run_at).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short' }) })}
                                  </span>
                                );
                              } else if (b.enabled) {
                                items.push(<span key='last' className='text-[10px] text-gray-600'>{t('supabase.backupPending')}</span>);
                              }
                              if (b.next_run_at) {
                                items.push(
                                  <span key='next' className='text-[10px] text-gray-500'>
                                    {t('supabase.nextRun', { date: new Date(b.next_run_at).toLocaleDateString('cs-CZ', { day: 'numeric', month: 'short' }) })}
                                  </span>
                                );
                              }
                              return items;
                            })()}
                            {inst.started_at && (
                              <span className='text-[10px] text-gray-500'>
                                {t('supabase.since', {
                                  date: new Date(inst.started_at).toLocaleDateString('cs-CZ', {
                                    day: 'numeric',
                                    month: 'short',
                                  }),
                                })}
                              </span>
                            )}
                            {pending && (
                              <span className='text-[10px] text-amber-400 flex items-center gap-1'>
                                <Loader2 size={10} className='animate-spin' />
                                {pending}
                              </span>
                            )}
                            {inst.error && (
                              <span className='text-[10px] text-red-400'>{inst.error}</span>
                            )}
                            {
                              /* Cert warning v hlavičce — zobraz JEN při problému (≤14d nebo expirováno).
                                Normální stav = autonomní NPM LE renewal, žádný vizuální šum.
                                Detail panel (expanded) ukazuje vždy oba certy pro audit. */
                            }
                            {inst.api_cert &&
                              (inst.api_cert.days_left <= 14 || !inst.api_cert.valid) && (
                              <CertBadge cert={inst.api_cert} label='API' />
                            )}
                            {inst.studio_cert &&
                              (inst.studio_cert.days_left <= 14 || !inst.studio_cert.valid) && (
                              <CertBadge cert={inst.studio_cert} label='Studio' />
                            )}
                          </div>

                          {/* Adresy: 2 řádky — API + Studio, každý má interní a veřejnou */}
                          <div className='text-xs space-y-0.5'>
                            {/* API */}
                            <div className='flex items-center gap-2 flex-wrap'>
                              <span className='text-gray-500 w-14 shrink-0 font-medium'>API</span>
                              {inst.private_api
                                ? (
                                  <span className='font-mono text-gray-400'>
                                    {inst.private_api}
                                  </span>
                                )
                                : <span className='text-gray-600'>—</span>}
                              {inst.api_public && inst.public_api
                                ? (
                                  <a
                                    href={inst.public_api}
                                    target='_blank'
                                    rel='noopener'
                                    className='flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-400 font-mono hover:bg-emerald-900/50'
                                  >
                                    <Globe size={10} />
                                    {inst.public_api.replace('https://', '')}
                                  </a>
                                )
                                : (
                                  <span className='flex items-center gap-1 text-gray-600'>
                                    <Lock size={10} />{t('supabase.private')}
                                  </span>
                                )}
                            </div>
                            {/* Studio */}
                            <div className='flex items-center gap-2 flex-wrap'>
                              <span className='text-gray-500 w-14 shrink-0 font-medium'>
                                Studio
                              </span>
                              {/* Adresa se vypisuje stejně jako u API řádku — Filip
                                  8. 9. 2026: „a tam by se měla pak doplnit ta adresa,
                                  presentování v jakém je režimu." Odkaz míří na ACM
                                  nginx wrapper (auth port), tedy PŘES PŘIHLÁŠENÍ;
                                  `raw_studio` na 127.0.0.1 by gate obešel. */}
                              {isActive && inst.private_studio
                                ? (() => {
                                  const authUrl = inst.auth_studio_port
                                    ? `https://${
                                      new URL(inst.private_studio).hostname
                                    }:${inst.auth_studio_port}/`
                                    : inst.private_studio;
                                  return (
                                    <a
                                      href={authUrl}
                                      target='_blank'
                                      rel='noopener'
                                      title={t('supabase.openStudioAuth')}
                                      className='flex items-center gap-1 font-mono text-blue-400 hover:text-blue-300'
                                    >
                                      <ExternalLink size={10} />
                                      {authUrl.replace('https://', '').replace(/\/$/, '')}
                                    </a>
                                  );
                                })()
                                : <span className='text-gray-600'>{t('supabase.stopped')}</span>}
                              {inst.studio_public && inst.public_studio
                                ? (
                                  <a
                                    href={inst.public_studio}
                                    target='_blank'
                                    rel='noopener'
                                    className='flex items-center gap-1 px-1.5 py-0.5 rounded bg-emerald-900/30 text-emerald-400 font-mono hover:bg-emerald-900/50'
                                  >
                                    <Globe size={10} />
                                    {inst.public_studio.replace('https://', '')}
                                  </a>
                                )
                                : (
                                  <span className='flex items-center gap-1 text-gray-600'>
                                    <Lock size={10} />{t('supabase.private')}
                                  </span>
                                )}
                              {/* Režim platí pro venkovní URL — bez ní není co omezovat. */}
                              {inst.studio_public && inst.public_studio && (
                                <Badge
                                  variant={inst.studio_access === 'public' ? 'success' : 'warning'}
                                >
                                  {inst.studio_access === 'public'
                                    ? t('supabase.studioAccessPublic')
                                    : t('supabase.studioAccessShadow')}
                                </Badge>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className='flex items-center gap-1'>
                          <div className='opacity-0 group-hover:opacity-100 transition-opacity flex items-center gap-1'>
                            <button
                              onClick={() => setLogViewer(inst.name)}
                              className='p-1.5 rounded-lg hover:bg-gray-700 text-gray-500 hover:text-gray-200 transition-colors'
                              title={t('supabase.deployLog')}
                            >
                              <FileText size={13} />
                            </button>
                            <button
                              onClick={() => setEditInst(inst)}
                              className='p-1.5 rounded-lg hover:bg-gray-700 text-gray-500 hover:text-gray-200 transition-colors'
                              title={t('supabase.settings')}
                            >
                              <Settings size={13} />
                            </button>
                            <LifecycleControls.Row
                              name={inst.name}
                              running={isActive}
                              pending={pending}
                              canDelete={!inst.legacy}
                              onAction={(name, action) => {
                                if (action === 'delete') setDeleteConfirm(name);
                                else manage.mutate({ name, action });
                              }}
                            />
                          </div>
                          <button
                            onClick={() => setExpandedInst(isExpanded ? null : inst.name)}
                            className='p-1.5 rounded-lg text-gray-600 hover:text-gray-300 hover:bg-gray-800 transition-colors ml-1'
                            title={t('supabase.containers')}
                          >
                            {isExpanded ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                          </button>
                        </div>
                      </div>

                      {/* Expandable container list */}
                      {isExpanded && (
                        <div className='border-t border-gray-800'>
                          <div className='flex items-center gap-2 px-4 py-2 bg-gray-900/40'>
                            <Container size={11} className='text-gray-600' />
                            <span className='text-[10px] text-gray-600 font-medium uppercase tracking-wider'>
                              {t('supabase.containersWithCount', { count: instContainers.length })}
                            </span>
                          </div>
                          {instContainers.length === 0
                            ? (
                              <div className='px-4 py-3 text-xs text-gray-600'>
                                {t('supabase.noContainersFound')}
                              </div>
                            )
                            : (
                              <div className='divide-y divide-gray-800/40'>
                                {instContainers.map((c) => {
                                  const cname = c.name.replace(/^\//, '');
                                  const shortId = (c.id ?? '').slice(0, 12);
                                  const running = c.state === 'running';
                                  const restarting = c.state === 'restarting';
                                  return (
                                    <div key={cname} className='flex items-center gap-3 px-4 py-2'>
                                      <div
                                        className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${
                                          running
                                            ? 'bg-emerald-400'
                                            : restarting
                                            ? 'bg-amber-400 animate-pulse'
                                            : 'bg-gray-600'
                                        }`}
                                      />
                                      <div className='flex-1 min-w-0'>
                                        <div
                                          className='text-xs font-mono text-gray-200 truncate'
                                          title={cname}
                                        >
                                          {cname}
                                        </div>
                                        <div className='text-[10px] font-mono text-gray-600 truncate'>
                                          {shortId ? `id ${shortId}` : ''}
                                          {c.image ? ` · ${c.image}` : ''}
                                        </div>
                                      </div>
                                      {c.ports && (
                                        <span className='text-[10px] font-mono text-gray-600 hidden sm:block'>
                                          {c.ports}
                                        </span>
                                      )}
                                      <span
                                        className={`text-[10px] px-1.5 py-0.5 rounded font-mono ${
                                          running
                                            ? 'text-emerald-500 bg-emerald-500/10'
                                            : restarting
                                            ? 'text-amber-400 bg-amber-400/10'
                                            : 'text-gray-600 bg-gray-800'
                                        }`}
                                      >
                                        {c.state}
                                      </span>
                                    </div>
                                  );
                                })}
                              </div>
                            )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
        </CardContent>
      </Card>

      {showCreate && (
        <CreateModal
          onClose={() => setShowCreate(false)}
          onDeployed={() => {
            setFastRefresh(true);
            setTimeout(() => setFastRefresh(false), 60_000);
          }}
        />
      )}
      {editInst && <EditModal inst={editInst} onClose={() => setEditInst(null)} />}
      <DeployLogViewer
        open={logViewer !== null}
        onClose={() => setLogViewer(null)}
        module='supabase'
        instance={logViewer ?? ''}
      />
      {deleteConfirm && (
        <DeleteConfirmModal
          name={deleteConfirm}
          onClose={() => setDeleteConfirm(null)}
          onConfirm={() => {
            del.mutate(deleteConfirm);
            setDeleteConfirm(null);
          }}
        />
      )}
    </div>
  );
}

// ── Schémata tab ──────────────────────────────────────────────────────────────

interface SchemaEntry {
  schema_name: string;
  table_count: number;
  data_size: string;
  in_pgrst: boolean;
}

interface SchemaListResult {
  ok: boolean;
  instance: string;
  schemas: SchemaEntry[];
  pgrst_schemas: string[];
  total: number;
}

interface SchemaCreds {
  ok: boolean;
  schema: string;
  schema_exists: boolean;
  supabase_url: string;
  anon_key: string;
  service_role_key: string;
  database_url: string;
  pooler_url: string | null;
  env: Record<string, string>;
}

function SchemaCredentials(
  { instance, schema, onClose }: { instance: string; schema: string; onClose: () => void },
) {
  const { t } = useTranslation();
  const { data, isLoading } = useQuery<SchemaCreds>({
    queryKey: ['supa-schema-creds', instance, schema],
    queryFn: () => callMcp('supabase.schema_credentials', { instance, schema }),
    staleTime: 60_000,
  });

  return (
    <BodyOverlay>
    <div className='fixed inset-0 bg-black/70 flex items-center justify-center p-4 z-50'>
      <div className='bg-gray-900 border border-gray-700 rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto'>
        <div className='flex items-center justify-between mb-5'>
          <h3 className='text-base font-semibold text-white flex items-center gap-2'>
            <Layers size={15} className='text-blue-400' />
            Credentials — <span className='font-mono text-blue-300'>{schema}</span>
          </h3>
          <button
            onClick={onClose}
            className='p-1.5 rounded-lg text-gray-500 hover:text-white hover:bg-gray-800'
          >
            <X size={16} />
          </button>
        </div>

        {isLoading && <div className='text-sm text-gray-500 py-4'>{t('supabase.loading')}</div>}
        {data && !data.ok && (
          <div className='text-sm text-red-400'>{t('supabase.credentialsLoadError')}</div>
        )}
        {data?.ok && (
          <div className='space-y-3'>
            {!data.schema_exists && (
              <div className='text-xs text-amber-400 bg-amber-400/10 border border-amber-500/20 rounded-lg px-3 py-2'>
                {t('supabase.schemaNotFoundWarn')}
              </div>
            )}
            <SecretField label='Supabase URL' value={data.supabase_url} />
            <SecretField label='Anon Key' value={data.anon_key} />
            <SecretField label='Service Role Key' value={data.service_role_key} />
            <SecretField label='Database URL (schema)' value={data.database_url} />
            {data.pooler_url && <SecretField label='Pooler URL' value={data.pooler_url} />}

            <div className='mt-4'>
              <p className='text-xs text-gray-500 font-medium mb-2'>.env snippet</p>
              <pre className='text-xs bg-gray-950 border border-gray-800 rounded-xl p-3 text-gray-300 font-mono whitespace-pre-wrap overflow-x-auto'>
                {Object.entries(data.env).map(([k, v]) => `${k}=${v}`).join('\n')}
              </pre>
              <button
                onClick={() =>
                  copyText(Object.entries(data.env).map(([k, v]) => `${k}=${v}`).join('\n'))}
                className='mt-2 text-xs text-gray-500 hover:text-gray-300 flex items-center gap-1'
              >
                <Copy size={11} /> {t('supabase.copyEnv')}
              </button>
            </div>
          </div>
        )}

        <div className='flex justify-end mt-5'>
          <Button variant='secondary' size='sm' onClick={onClose}>{t('supabase.close')}</Button>
        </div>
      </div>
    </div>
    </BodyOverlay>
  );
}

function SchemataTab() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [credsFor, setCredsFor] = useState<{ instance: string; schema: string } | null>(null);
  const [newSchema, setNewSchema] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [expanded, setExpanded] = useState<string | null>(null);

  // Načti seznam instancí
  const { data: instData } = useQuery<
    { ok: boolean; instances: Array<{ name: string; status: string }> }
  >({
    queryKey: ['supabase-instances', scopeKey()],
    queryFn: () => callMcp('supabase.list', {}),
    staleTime: 30_000,
  });

  const instances = (instData?.instances ?? []).filter((i) =>
    i.status === 'healthy' || i.status === 'running'
  );

  const activeInstance = selectedInstance || instances[0]?.name || '';

  const { data: schemaData, isLoading, refetch } = useQuery<SchemaListResult>({
    queryKey: ['supa-schemas', activeInstance],
    queryFn: () => callMcp('supabase.schema_list', { instance: activeInstance }),
    enabled: !!activeInstance,
    staleTime: 30_000,
  });

  const createMut = useMutation({
    mutationFn: () =>
      callMcp<{ ok: boolean; error?: string }>(
        'supabase.schema_create',
        { instance: activeInstance, schema: newSchema },
      ),
    onSuccess: (r: { ok: boolean; error?: string }) => {
      if (!r.ok) {
        toast.error(r.error ?? t('supabase.createFailed'));
        return;
      }
      toast.success(t('supabase.schemaCreated', { name: newSchema }));
      setNewSchema('');
      setShowCreate(false);
      qc.invalidateQueries({ queryKey: ['supa-schemas', activeInstance] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const deleteMut = useMutation({
    mutationFn: (schema: string) =>
      callMcp<{ ok: boolean; error?: string }>(
        'supabase.schema_delete',
        { instance: activeInstance, schema },
      ),
    onSuccess: (r: { ok: boolean; error?: string }, schema) => {
      if (!r.ok) {
        toast.error(r.error ?? t('supabase.deleteFailed'));
        return;
      }
      toast.success(t('supabase.schemaDeleted', { name: schema }));
      qc.invalidateQueries({ queryKey: ['supa-schemas', activeInstance] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const slugOk = /^[a-z][a-z0-9_]{1,30}$/.test(newSchema);

  return (
    <div className='space-y-5'>
      <StatsGrid columns={3}>
        <StatCard
          icon={Database}
          label={t('supabase.statInstance')}
          value={instances.length}
          sub={t('supabase.statActiveSub')}
          variant='info'
        />
        <StatCard
          icon={Layers}
          label={t('supabase.statSchemas')}
          value={schemaData?.total ?? '—'}
          sub={t('supabase.statInInstanceSub')}
        />
        <StatCard
          icon={Table2}
          label={t('supabase.statInPostgrest')}
          value={schemaData?.pgrst_schemas?.length ?? '—'}
          sub={t('supabase.statApiAccessibleSub')}
          variant='success'
        />
      </StatsGrid>

      <Card>
        <CardHeader>
          <div className='flex items-center gap-3 flex-1'>
            <CardTitle icon={Layers}>{t('supabase.schemasTitle')}</CardTitle>
            {instances.length > 0 && (
              <Select
                size='sm'
                value={activeInstance}
                onChange={(e) => setSelectedInstance(e.target.value)}
              >
                {instances.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
              </Select>
            )}
          </div>
          <div className='flex items-center gap-2'>
            <Button variant='ghost' size='icon' onClick={() => refetch()} title={t('supabase.refresh')}>
              <RefreshCw size={13} />
            </Button>
            <Button
              variant='primary'
              size='sm'
              icon={<Plus size={13} />}
              onClick={() => setShowCreate((s) => !s)}
            >
              {t('supabase.newSchema')}
            </Button>
          </div>
        </CardHeader>

        {showCreate && (
          <div className='border-b border-gray-800 px-5 py-4 bg-gray-900/40'>
            <div className='flex items-end gap-3'>
              <div className='flex-1'>
                <label className='text-xs text-gray-400 mb-1 block'>
                  {t('supabase.schemaName')} <span className='text-gray-600'>(a-z, 0-9, _)</span>
                </label>
                <input
                  value={newSchema}
                  onChange={(e) =>
                    setNewSchema(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ''))}
                  placeholder='my_project'
                  className={`w-full px-3 py-2 rounded-lg bg-gray-800 border text-white text-sm focus:outline-none ${
                    newSchema && !slugOk
                      ? 'border-red-500'
                      : 'border-gray-700 focus:border-blue-500'
                  }`}
                />
              </div>
              <Button
                variant='primary'
                size='sm'
                loading={createMut.isPending}
                disabled={!newSchema || !slugOk}
                onClick={() => createMut.mutate()}
              >
                {t('supabase.create')}
              </Button>
              <Button
                variant='secondary'
                size='sm'
                onClick={() => {
                  setShowCreate(false);
                  setNewSchema('');
                }}
              >
                {t('supabase.cancel')}
              </Button>
            </div>
          </div>
        )}

        <CardContent>
          {!activeInstance && (
            <p className='text-sm text-gray-500 py-4'>{t('supabase.noActiveInstance')}</p>
          )}
          {isLoading && <p className='text-sm text-gray-500 py-4'>{t('supabase.loadingSchemas')}</p>}
          {schemaData?.ok && schemaData.schemas.length === 0 && (
            <p className='text-sm text-gray-500 py-4'>{t('supabase.noUserSchemas')}</p>
          )}
          {schemaData?.ok && schemaData.schemas.length > 0 && (
            <div className='space-y-1'>
              {schemaData.schemas.map((s) => (
                <div
                  key={s.schema_name}
                  className='border border-gray-800 rounded-xl overflow-hidden'
                >
                  <div
                    className='flex items-center gap-3 px-4 py-3 cursor-pointer hover:bg-gray-800/40 group'
                    onClick={() => setExpanded((e) => e === s.schema_name ? null : s.schema_name)}
                  >
                    <Layers size={13} className='text-blue-400 flex-shrink-0' />
                    <span className='font-mono text-sm text-white flex-1'>{s.schema_name}</span>
                    <span className='text-xs text-gray-500 flex items-center gap-1'>
                      <Table2 size={10} /> {s.table_count}
                    </span>
                    <span className='text-xs text-gray-600'>{s.data_size}</span>
                    {s.in_pgrst
                      ? (
                        <span className='text-[10px] px-1.5 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20'>
                          PostgREST
                        </span>
                      )
                      : (
                        <span className='text-[10px] px-1.5 py-0.5 rounded bg-gray-800 text-gray-600 border border-gray-700'>
                          {t('supabase.private')}
                        </span>
                      )}
                    {expanded === s.schema_name
                      ? <ChevronUp size={13} className='text-gray-500' />
                      : <ChevronDown size={13} className='text-gray-500' />}
                  </div>

                  {expanded === s.schema_name && (
                    <div className='border-t border-gray-800 px-4 py-3 bg-gray-900/40 flex items-center gap-2'>
                      <Button
                        variant='secondary'
                        size='sm'
                        icon={<Copy size={11} />}
                        onClick={() =>
                          setCredsFor({ instance: activeInstance, schema: s.schema_name })}
                      >
                        Credentials
                      </Button>
                      <Button
                        variant='danger'
                        size='sm'
                        icon={<Trash2 size={11} />}
                        loading={deleteMut.isPending}
                        onClick={async () => {
                          if (
                            await themedConfirm(t('supabase.confirmDeleteSchema', { name: s.schema_name }))
                          ) {
                            deleteMut.mutate(s.schema_name);
                          }
                        }}
                      >
                        {t('supabase.delete')}
                      </Button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {credsFor && (
        <SchemaCredentials
          instance={credsFor.instance}
          schema={credsFor.schema}
          onClose={() => setCredsFor(null)}
        />
      )}
    </div>
  );
}

// ── ACL Users Tab ─────────────────────────────────────────────────────────────

interface AclEntry {
  id: number;
  keycloak_sub: string;
  keycloak_username: string | null;
  role: string;
  granted_by: string | null;
  granted_at: string;
}

interface AcmUserWithSub {
  id: string;
  username: string;
  email: string | null;
  keycloak_sub: string;
}

function UsersTab() {
  const { t } = useTranslation();
  const [selectedInstance, setSelectedInstance] = useState<string>('');
  const [addOpen, setAddOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AcmUserWithSub | null>(null);
  const [role, setRole] = useState<'owner' | 'editor' | 'viewer'>('viewer');

  // Seznam instancí pro dropdown
  const { data: instancesData } = useQuery<
    { instances?: Array<{ name: string; legacy?: boolean }> }
  >({
    queryKey: ['supabase-instances', scopeKey()],
    queryFn: () => callMcp('supabase.list', {}),
  });
  const instances = (instancesData?.instances ?? []).filter((i) => !i.legacy);

  // Auto-select first instance
  useEffect(() => {
    if (!selectedInstance && instances.length > 0) setSelectedInstance(instances[0]!.name);
  }, [instances, selectedInstance]);

  const { data: aclData, refetch: refetchAcl } = useQuery<{ ok: boolean; acl: AclEntry[] }>({
    queryKey: ['supabase-acl', selectedInstance],
    queryFn: () =>
      callMcp('supabase.acl', {
        action: 'list',
        instance: selectedInstance!,
      }),
    enabled: !!selectedInstance,
  });
  const acl = aclData?.acl ?? [];

  // Keycloak search — centrální registr identit (Keycloak je autoritativní).
  // Nemusí existovat pair v acm.users; stačí existence uživatele v Keycloak realmu.
  const [userSearch, setUserSearch] = useState('');
  const { data: kcUsersData, isFetching: kcSearching } = useQuery<
    { ok: boolean; users: Array<{ id: string; username: string; email?: string }> }
  >({
    queryKey: ['keycloak-users-search', userSearch],
    queryFn: () =>
      callMcp('sso.keycloak_list_users', { search: userSearch || undefined, max: 50 }),
    enabled: addOpen,
    staleTime: 5000,
  });
  const kcUsers = kcUsersData?.users ?? [];
  const eligibleUsers: AcmUserWithSub[] = kcUsers.map((u) => ({
    id: u.id,
    username: u.username,
    email: u.email ?? null,
    keycloak_sub: u.id,
  }));

  const addAcl = useMutation({
    mutationFn: (body: { keycloak_sub: string; keycloak_username?: string; role: string }) =>
      callMcp('supabase.acl', {
        action: 'add',
        instance: selectedInstance,
        ...body,
      }),
    onSuccess: () => {
      toast.success(t('supabase.userAddedToAcl'));
      setAddOpen(false);
      setSelectedUser(null);
      refetchAcl();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const delAcl = useMutation({
    mutationFn: (sub: string) =>
      callMcp('supabase.acl', {
        action: 'delete',
        instance: selectedInstance,
        keycloak_sub: sub,
      }),
    onSuccess: () => {
      toast.success(t('supabase.removed'));
      refetchAcl();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const handleAdd = () => {
    if (!selectedUser) return toast.error(t('supabase.selectUser'));
    addAcl.mutate({
      keycloak_sub: selectedUser.keycloak_sub,
      keycloak_username: selectedUser.username,
      role,
    });
  };

  const roleBadge = (r: string): 'error' | 'warning' | 'secondary' | 'default' => {
    if (r === 'owner') return 'error';
    if (r === 'editor') return 'warning';
    if (r === 'viewer') return 'secondary';
    return 'default';
  };

  if (instances.length === 0) {
    return (
      <EmptyState
        icon={<Database size={24} />}
        title={t('supabase.noInstancesTitle')}
        description={t('supabase.noInstancesDesc')}
      />
    );
  }

  return (
    <div className='space-y-4'>
      {/* Instance selector */}
      <Card>
        <CardContent className='py-3'>
          <div className='flex items-center gap-3'>
            <span className='text-xs text-gray-500'>{t('supabase.instanceLabel')}</span>
            <Select
              className='flex-1'
              value={selectedInstance}
              onChange={(e) => setSelectedInstance(e.target.value)}
            >
              {instances.map((i) => <option key={i.name} value={i.name}>{i.name}</option>)}
            </Select>
            <Button
              variant='primary'
              size='sm'
              icon={<UserPlus size={14} />}
              onClick={() => setAddOpen(true)}
              disabled={!selectedInstance}
            >
              {t('supabase.addUser')}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* ACL table */}
      {acl.length === 0
        ? (
          <EmptyState
            icon={<Users size={24} />}
            title={t('supabase.noAclTitle')}
            description={t('supabase.noAclDesc', { name: selectedInstance })}
          />
        )
        : (
          <Card>
            <CardContent className='p-0'>
              <table className='w-full text-sm'>
                <thead className='border-b border-gray-800 text-left text-xs text-gray-500 uppercase tracking-wider'>
                  <tr>
                    <th className='px-4 py-3'>{t('supabase.colUser')}</th>
                    <th className='px-4 py-3'>Keycloak sub</th>
                    <th className='px-4 py-3 w-24'>Role</th>
                    <th className='px-4 py-3'>{t('supabase.colAssigned')}</th>
                    <th className='px-4 py-3 w-20'></th>
                  </tr>
                </thead>
                <tbody>
                  {acl.map((e) => (
                    <tr
                      key={e.id}
                      className='border-b border-gray-800/50 last:border-0 hover:bg-gray-800/20'
                    >
                      <td className='px-4 py-3 text-gray-300'>{e.keycloak_username ?? '—'}</td>
                      <td className='px-4 py-3 font-mono text-xs text-gray-500'>
                        {e.keycloak_sub.slice(0, 8)}…
                      </td>
                      <td className='px-4 py-3'>
                        <Badge variant={roleBadge(e.role)}>{e.role}</Badge>
                      </td>
                      <td className='px-4 py-3 text-xs text-gray-500'>
                        {new Date(e.granted_at).toLocaleDateString('cs-CZ')}{' '}
                        {new Date(e.granted_at).toLocaleTimeString('cs-CZ', {
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </td>
                      <td className='px-4 py-3 text-right'>
                        <button
                          onClick={async () => {
                            if (
                              await themedConfirm(
                                t('supabase.confirmRemoveUser', { user: e.keycloak_username ?? e.keycloak_sub }),
                              )
                            ) delAcl.mutate(e.keycloak_sub);
                          }}
                          className='text-gray-600 hover:text-red-400 p-1'
                          title={t('supabase.remove')}
                        >
                          <Trash2 size={12} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </CardContent>
          </Card>
        )}

      {/* Add user modal */}
      <Modal
        open={addOpen}
        onClose={() => {
          setAddOpen(false);
          setSelectedUser(null);
        }}
        title={t('supabase.addUserToAclTitle', { name: selectedInstance })}
        footer={
          <>
            <Button variant='secondary' onClick={() => setAddOpen(false)}>{t('supabase.cancel')}</Button>
            <Button
              variant='primary'
              onClick={handleAdd}
              loading={addAcl.isPending}
              disabled={!selectedUser}
            >
              {t('supabase.add')}
            </Button>
          </>
        }
      >
        <div className='space-y-3'>
          <div>
            <Label>{t('supabase.searchKeycloakUser')}</Label>
            <input
              type='text'
              className='mt-1 w-full bg-gray-950 border border-gray-800 rounded-lg px-3 py-1.5 text-sm text-white'
              value={userSearch}
              onChange={(e) => {
                setUserSearch(e.target.value);
                setSelectedUser(null);
              }}
              placeholder={t('supabase.userSearchPlaceholder')}
              autoFocus
            />
            <p className='text-[11px] text-gray-500 mt-1'>
              {t('supabase.keycloakSearchHint')}
            </p>
          </div>
          <div>
            <Label>{t('supabase.userLabel')} {kcSearching ? t('supabase.searching') : `(${eligibleUsers.length})`}</Label>
            <Select
              className='mt-1 w-full'
              value={selectedUser?.id ?? ''}
              onChange={(e) =>
                setSelectedUser(eligibleUsers.find((u) => u.id === e.target.value) ?? null)}
            >
              {eligibleUsers.length === 0 && !kcSearching && (
                <option value='' disabled>{t('supabase.noUserFound')}</option>
              )}
              {eligibleUsers.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.username}
                  {u.email ? ` — ${u.email}` : ''}
                </option>
              ))}
            </Select>
          </div>
          <div>
            <Label>Role</Label>
            <Select
              className='mt-1 w-full'
              value={role}
              onChange={(e) => setRole(e.target.value as 'owner' | 'editor' | 'viewer')}
            >
              <option value='owner'>{t('supabase.roleOwner')}</option>
              <option value='editor'>{t('supabase.roleEditor')}</option>
              <option value='viewer'>{t('supabase.roleViewer')}</option>
            </Select>
          </div>
          <div className='p-3 bg-blue-950/20 border border-blue-500/20 rounded-xl text-xs text-blue-300'>
            Keycloak sub{' '}
            <code className='font-mono'>{selectedUser?.keycloak_sub.slice(0, 16) ?? '—'}…</code>
            {' '}
            {t('supabase.subWillHaveAccess')}
          </div>
        </div>
      </Modal>
    </div>
  );
}

// ── Plugin export — volá orchestrator/infrastruktura přes servicePlugins ─────
/**
 * SupabaseServiceEditor — pluginová obálka pro EditModal.
 * Přijme `name` (orchestrator service name nebo supabase instance name),
 * vyhledá v supabase.list a otevře EditModal.
 */
export function SupabaseServiceEditor(
  { name, onClose }: { name: string; instance?: string; kind?: string; onClose: () => void },
) {
  const { t } = useTranslation();
  const { data } = useQuery<SupabaseListResult>({
    queryKey: ['supabase-list-plugin', name],
    queryFn: () => callMcp('supabase.list', {}),
  });
  const inst = data?.instances?.find((i) =>
    i.name === name || `${i.name}-backend` === name || i.url_hash === name
  );
  if (!inst) {
    return (
      <div className='p-6 text-sm text-gray-400'>
        <p>{t('supabase.instanceNotFoundPre')} <code>{name}</code> {t('supabase.instanceNotFoundPost')}</p>
        <p className='text-xs mt-1 text-gray-500'>
          {t('supabase.checkServiceNameMatch')}
        </p>
        <button onClick={onClose} className='mt-4 px-3 py-1 bg-gray-800 rounded text-xs'>
          {t('supabase.close')}
        </button>
      </div>
    );
  }
  return <EditModal inst={inst} onClose={onClose} />;
}


// ── Widget entrypoints (WidgetShell → component) ─────────────────────────────

export function InstancesPanel({ scope }: WidgetRenderProps) {
  setSupabaseWidgetScope(scope ?? { node: null, identity: null });
  return <InstancesTab key={scopeKey()} />;
}

export function SchemataPanel({ scope }: WidgetRenderProps) {
  setSupabaseWidgetScope(scope ?? { node: null, identity: null });
  return <SchemataTab key={scopeKey()} />;
}

export function UsersPanel({ scope }: WidgetRenderProps) {
  setSupabaseWidgetScope(scope ?? { node: null, identity: null });
  return <UsersTab key={scopeKey()} />;
}
